import { forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Routes } from '@angular/router';
import { featureOn } from '../../feature-flag';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';
import { provideChildTranslateService } from '@ngx-translate/core';
import { BreadcrumbsEnum, BreadcrumbsMenuContextEnum } from '../shared/models/breadcrumbs.enum';
import { apolloLinkProvider } from '../shared/apollo-links/apollo-link.provider';
import { sharedReducer } from '../shared/store/shared.reducer';
import { sharedFeatureKey } from '../shared/store/shared.state';
import { unsavedChangesGuard } from '../../services/unsaved-changes/unsaved-changes.guard';
import { TrashEntityTypeEnum } from '@libs/types/src/services/enum/trash-entity-type.enum';
import { ContentComponent } from './content.component';
import { SelectColorComponent } from './components/common/select-color/select-color.component';
import { ServicesGroupsPageComponent } from './pages/services/services-groups-page/services-groups-page.component';
import {
    ServicesBasicInformationPageComponent,
} from './pages/services/services-basic-information-page/services-basic-information-page.component';
import {
    ServicesSettingsPageComponent,
} from './pages/services/services-settings-page/services-settings-page.component';
import { OrganizationsPageComponent } from './pages/organization/organizations-page/organizations-page.component';
import {
    CreateOrganizationPageComponent,
} from './pages/organization/create-organization-page/create-organization-page.component';
import {
    OrganizationDetailPageComponent,
} from './pages/organization/organization-detail-page/organization-detail-page.component';
import { CenterDetailPageComponent } from './pages/organization/center-detail-page/center-detail-page.component';
import { ServiceDetailPageComponent } from './pages/services/service-detail-page/service-detail-page.component';
import { ServicesPageComponent } from './pages/services/services-page/services-page.component';
import {
    ServiceSettingsFormsComponent,
} from './pages/services/settings/service-settings-forms/service-settings-forms.component';
import {
    ServicesSettingsGeneralInfoPageComponent,
} from './pages/services/services-settings-general-info-page/services-settings-general-info-page.component';
import { UsersPageComponent } from './pages/user-management/users-page/users-page.component';
import { UserPageComponent } from './pages/user-management/user-page/user-page.component';
import { AclGroupsPageComponent } from './pages/user-management/acl-groups-page/acl-groups-page.component';
import { ProfilePageComponent } from './pages/profile-page/profile-page.component';
import { WorkplaceDetailPageComponent } from './pages/services/workplace-detail-page/workplace-detail-page.component';
import {
    ServiceSettingsDoctorRecordComponent,
} from './pages/services/settings/service-settings-doctor-record/service-settings-doctor-record.component';
import {
    ServiceSettingsSpecialistRecordComponent,
} from './pages/services/settings/service-settings-specialist-record/service-settings-specialist-record.component';
import {
    ServicesProceduresDetailPageComponent,
} from './pages/services/services-procedures-page/services-procedures-detail-page/services-procedures-detail-page.component';
import {
    ServicesProceduresPageComponent,
} from './pages/services/services-procedures-page/services-procedures-page.component';
import {
    ServicesPaymentsPageComponent,
} from './pages/services/services-payments-page/services-payments-page.component';
import {
    ServicesPaymentsDetailPageComponent,
} from './pages/services/services-payments-page/services-payments-detail-page/services-payments-detail-page.component';
import { ServicePatternsPageComponent } from './pages/services/service-patterns-page/service-patterns-page.component';
import {
    ServicesPatternsStructurePageComponent,
} from './pages/services/service-patterns-page/services-patterns-structure-page/services-patterns-structure-page.component';
import {
    ServicesPatternsDocumentPageComponent,
} from './pages/services/service-patterns-page/services-patterns-document-page/services-patterns-document-page.component';
import {
    ServiceOperationalReportsPageComponent,
} from './pages/services/service-operational-reports-page/service-operational-reports-page.component';
import {
    ServiceNursingDiagnosesPageComponent,
} from './pages/services/service-nursing-diagnoses-page/service-nursing-diagnoses-page.component';
import {
    ServiceNursingDiagnosisDetailPageComponent,
} from './pages/services/service-nursing-diagnoses-page/service-nursing-diagnosis-detail-page/service-nursing-diagnosis-detail-page.component';
import {
    ServiceSettingsRiskTypeComponent,
} from './pages/services/settings/service-settings-risk-type/service-settings-risk-type.component';
import {
    ToolsMissingProceduresPageComponent,
} from './pages/services/tools-missing-procedures-page/tools-missing-procedures-page.component';
import {
    ToolsMissingDoctorRecordsPageComponent,
} from './pages/services/tools-missing-doctor-records-page/tools-missing-doctor-records-page.component';
import { DashboardPageComponent } from './pages/dashboard/dashboard-page/dashboard-page.component';
import {
    ServiceSettingsContactsComponent,
} from './pages/services/settings/service-settings-contacts/service-settings-contacts.component';
import {
    ServiceSettingsVoucherComponent,
} from './pages/services/settings/service-settings-voucher/service-settings-voucher.component';
import {
    ServiceSettingsEpicrisisComponent,
} from './pages/services/settings/service-settings-epicrisis/service-settings-epicrisis.component';
import {
    ServiceSettingsCarePlanComponent,
} from './pages/services/settings/service-settings-care-plan/service-settings-care-plan.component';
import {
    ServiceSettingsAnamnesisComponent,
} from './pages/services/settings/service-settings-anamnesis/service-settings-anamnesis.component';
import {
    ServiceSettingsMedicationComponent,
} from './pages/services/settings/service-settings-medication/service-settings-medication.component';
import {
    ServiceSettingsNurseRecordsComponent,
} from './pages/services/settings/service-settings-nurse-records/service-settings-nurse-records.component';
import {
    ServiceSettingsSchedulerComponent,
} from './pages/services/settings/service-settings-scheduler/service-settings-scheduler.component';
import {
    MedicalServiceBatchPrintPageComponent,
} from '../../components/medical-service/tool/batch-print/medical-service-batch-print-page/medical-service-batch-print-page.component';
import {
    ServiceSettingsEPrescriptionsComponent,
} from './pages/services/settings/service-settings-eprescriptions/service-settings-eprescriptions.component';
import {
    ServiceSettingsSickNotesComponent,
} from './pages/services/settings/service-settings-sick-notes/service-settings-sick-notes.component';
import {
    ServiceSettingsEpoukazComponent,
} from './pages/services/settings/service-settings-epoukaz/service-settings-epoukaz.component';
import {
    MedicalServiceLogsPageComponent,
} from '../../components/medical-service/setting/medical-service-logs-page/medical-service-logs-page.component';
import {
    ServiceSettingsVisitTypeComponent,
} from './pages/services/settings/service-settings-visit-type/service-settings-visit-type.component';
import {
    ServiceSettingsMeasuredValuesComponent,
} from './pages/services/settings/service-settings-measured-values/service-settings-measured-values.component';
import { EmptyPageComponent } from '../../components/kit/empty-page/empty-page.component';
import {
    ServiceSettingDashboardPageComponent,
} from '../../components/social-service/setting/service-setting-dashboard-page/service-setting-dashboard-page.component';
import {
    SystemSettingsPageComponent,
} from '../../components/system-setting/system-settings-page/system-settings-page.component';
import {
    ServiceTypesPageComponent,
} from '../../components/system-setting/service-type/service-types-page/service-types-page.component';
import {
    ServiceTypeDetailPageComponent,
} from '../../components/system-setting/service-type/service-type-detail-page/service-type-detail-page.component';
import {
    ServiceTypeOffersPageComponent,
} from '../../components/system-setting/service-type/service-type-offers-page/service-type-offers-page.component';
import {
    ServiceTypeSettingsPageComponent,
} from '../../components/system-setting/service-type/service-type-settings-page/service-type-settings-page.component';
import {
    ServiceSettingsDocumentsComponent,
} from './pages/services/settings/service-settings-documents/service-settings-documents.component';
import {
    ServiceSettingsOffersPageComponent,
} from '../../components/social-service/setting/service-settings-offers-page/service-settings-offers-page.component';
import {
    ServiceSettingsOfferDetailPageComponent,
} from '../../components/social-service/setting/service-settings-offers-page/service-settings-offer-detail-page/service-settings-offer-detail-page.component';
import {
    ServiceSettingContinuousRecordPageComponent,
} from '../../components/social-service/setting/service-setting-continuous-record-page/service-setting-continuous-record-page.component';
import {
    ServiceSettingsMovementRestrictionComponent,
} from '../../components/social-service/setting/service-settings-movement-restriction/service-settings-movement-restriction.component';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { SocialStatisticsTypeEnum } from '@libs/types/src/services/enum/statistics/social-statistics-type.enum';
import {
    ServiceSettingSanctionComponent,
} from '../../components/social-service/setting/service-setting-sanction/service-setting-sanction.component';
import {
    ServiceSettingSuggestionComponent,
} from '../../components/social-service/setting/service-setting-suggestion/service-setting-suggestion.component';
import { HealthCareRealizedWorkRedirectGuardGuard } from './guards/health-care-realized-work-redirect-guard.guard';
import { ServiceIdGuard } from './guards/service-id.guard';
import { planningCalendarGuard } from './guards/planning-calendar.guard';
import { PersonService } from './service/person.service';
import { ServiceService } from './service/service.service';
import { SporitelnaService } from './service/sporitelna.service';
import { UserService } from './service/user.service';
import { organizationReducer } from './store/organization/organization.reducer';
import { organizationFeatureKey } from './store/organization/organization.state';
import { PersonEffects } from './store/person/person.effects';
import { personReducer } from './store/person/person.reducer';
import { personFeatureKey } from './store/person/person.state';
import { ServiceEffects } from './store/service/service.effects';
import { serviceReducer } from './store/service/service.reducer';
import { serviceFeatureKey } from './store/service/service.state';
import { UserEffects } from './store/user/user.effects';
import { userReducer } from './store/user/user.reducer';
import { userFeatureKey } from './store/user/user.state';
import { UpsertPersonPageComponent } from './pages/person/upsert-person-page/upsert-person-page.component';
import { FolderDashboardPageComponent } from './pages/person/folder-dashboard-page/folder-dashboard-page.component';
import {
    PersonContactsPageComponent,
} from './pages/person/person-contacts/person-contacts-page/person-contacts-page.component';
import { FolderAnamnesisPageComponent } from './pages/person/folder-anamnesis-page/folder-anamnesis-page.component';
import { FolderEpicrisisPageComponent } from './pages/person/folder-epicrisis-page/folder-epicrisis-page.component';
import { FolderCarePlanPageComponent } from './pages/person/folder-care-plan-page/folder-care-plan-page.component';
import {
    FolderMedicationsPageComponent,
} from './pages/person/folder-medications-page/folder-medications-page.component';
import {
    FolderEPrescriptionsPageComponent,
} from '../../components/medical-service/folder/folder-e-prescriptions/folder-e-prescriptions-page/folder-e-prescriptions-page.component';
import {
    FolderEPrescriptionCreateComponent,
} from '../../components/medical-service/folder/folder-e-prescriptions/folder-e-prescription-create/folder-e-prescription-create.component';
import {
    FolderEPrescriptionDetailComponent,
} from '../../components/medical-service/folder/folder-e-prescriptions/folder-e-prescription-detail/folder-e-prescription-detail.component';
import {
    FolderServiceRecordsPageComponent,
} from './pages/person/folder-service-records-page/folder-service-records-page.component';
import { VouchersPageComponent } from './pages/person/vouchers-page/vouchers-page.component';
import { VoucherDetailPageComponent } from './pages/person/voucher-detail-page/voucher-detail-page.component';
import {
    VoucherNursingCarePlanDetailPageComponent,
} from './pages/person/voucher-nursing-care-plan-detail-page/voucher-nursing-care-plan-detail-page.component';
import {
    FolderNurseRecordsPageComponent,
} from './pages/person/folder-nurse-records-page/folder-nurse-records-page.component';
import {
    FolderUnrealizedVisitPageComponent,
} from './pages/person/folder-unrealized-visit-page/folder-unrealized-visit-page.component';
import {
    FolderExtramuralCarePageComponent,
} from './pages/person/folder-extramural-care-page/folder-extramural-care-page.component';
import {
    FolderDoctorRecordsPageComponent,
} from './pages/person/folder-doctor-records-page/folder-doctor-records-page.component';
import {
    FolderMedicalReportPageComponent,
} from './pages/person/folder-medical-report-page/folder-medical-report-page.component';
import {
    FolderSpecialistRecordsPageComponent,
} from './pages/person/folder-specialist-records-page/folder-specialist-records-page.component';
import { FolderFormsPageComponent } from './pages/person/folder-forms-page/folder-forms-page.component';
import {
    FolderSickNotesPageComponent,
} from '../../components/medical-service/folder/folder-sick-notes/folder-sick-notes-page/folder-sick-notes-page.component';
import {
    FolderSickNotesDetailPageComponent,
} from '../../components/medical-service/folder/folder-sick-notes/folder-sick-notes-detail-page/folder-sick-notes-detail-page.component';
import {
    FolderContinuousRecordComponent,
} from '../../components/social-service/folder/folder-continuous-record/folder-continuous-record/folder-continuous-record.component';
import {
    FolderOperationalReportPageComponent,
} from '../../components/common-service/folder/folder-operational-report-page/folder-operational-report-page.component';
import {
    FolderDocumentsPageComponent,
} from './pages/person/folder-documents/folder-documents-page/folder-documents-page.component';
import {
    WorkMovementRestrictionsPageComponent,
} from '../../components/social-service/work/pages/work-movement-restrictions-page/work-movement-restrictions-page.component';
import {
    WorkSanctionsPageComponent,
} from '../../components/social-service/work/pages/work-sanctions-page/work-sanctions-page.component';
import {
    FolderSuggestionsPageComponent,
} from '../../components/social-service/folder/suggestions/folder-suggestions-page.component';
import { ContractsGuard } from '../../components/social-service/folder/contracts/contracts.guard';
import { WorkDataSuggestionTypeEnum } from '@libs/types/src/services/enum/work-data-suggestion-type.enum';
import { FolderWorksPageComponent } from '../../components/social-service/folder/works/folder-works-page.component';

const xCarolName = 'IS XCarol - komplexní informační systém';
const generalName = 'IS';

const billingDetailRoutes: Routes = [
    {
        path: 'invoice/:invoiceId',
        title: xCarolName,
        data: {
            breadcrumb: {
                label: BreadcrumbsEnum.BillingInvoiceDetail,
                alias: BreadcrumbsEnum.BillingInvoiceDetail,
            },
        },
        loadComponent: () =>
            import(
                '../../components/medical-service/billing/billing-invoice-detail/billing-invoice-detail-page/billing-invoice-detail-page.component'
                ).then((m) => m.BillingInvoiceDetailPageComponent),
    },
    {
        path: 'batch/:billingBatchId',
        title: xCarolName,
        data: {
            breadcrumb: {
                label: BreadcrumbsEnum.BillingBatchDetail,
                alias: BreadcrumbsEnum.BillingBatchDetail,
            },
        },
        loadComponent: () =>
            import(
                '../../components/medical-service/billing/billing-batch-detail/billing-batch-detail-page/billing-batch-detail-page.component'
                ).then((m) => m.BillingBatchDetailPageComponent),
    },
    {
        path: 'batch/:billingBatchId',
        title: xCarolName,
        data: {
            breadcrumb: {
                label: BreadcrumbsEnum.BillingBatchDetail,
                alias: BreadcrumbsEnum.BillingBatchDetail,
            },
        },
        children: [
            {
                path: 'document/:billingDocumentId',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.BillingDocumentDetail,
                        alias: BreadcrumbsEnum.BillingDocumentDetail,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/billing/billing-document-detail/billing-document-detail-page/billing-document-detail-page.component'
                        ).then((m) => m.BillingDocumentDetailPageComponent),
            },
        ],
    },
];

const baseServiceTypeRoutes: Routes = [
    {
        path: 'folders',
        title: xCarolName,
        data: {
            trashEntityName: TrashEntityTypeEnum.FOLDER,
            breadcrumb: {
                label: BreadcrumbsEnum.Folders,
                alias: BreadcrumbsEnum.Folders,
            },
        },
        loadComponent: () =>
            import('./pages/person/folders-page/folders-page.component').then(
                (m) => m.FoldersPageComponent,
            ),
    },
    {
        path: 'folders',
        title: xCarolName,
        data: {
            breadcrumb: {
                label: BreadcrumbsEnum.Folders,
                alias: BreadcrumbsEnum.Folders,
            },
        },
        children: [
            {
                path: 'create',
                title: xCarolName,
                data: {
                    visit: { skip: true },
                    breadcrumb: {
                        label: BreadcrumbsEnum.CreatePerson,
                        alias: BreadcrumbsEnum.CreatePerson,
                    },
                },
                loadComponent: () =>
                    import(
                        './pages/person/upsert-person-page/upsert-person-page.component'
                        ).then((m) => m.UpsertPersonPageComponent),
            },
            {
                path: ':folderId',
                title: xCarolName,
                data: {
                    visit: { group: true, skip: true },
                    breadcrumbMenuContext: BreadcrumbsMenuContextEnum.FOLDER,
                    breadcrumb: {
                        label: BreadcrumbsEnum.Folder,
                        alias: BreadcrumbsEnum.Folder,
                    },
                },
                loadComponent: () =>
                    import(
                        './pages/person/folder-detail-page/folder-detail-page.component'
                        ).then((m) => m.FolderDetailPageComponent),
                children: [
                    {
                        path: 'edit',
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderEdit,
                                alias: BreadcrumbsEnum.FolderEdit,
                            },
                        },
                        title: xCarolName,
                        component: UpsertPersonPageComponent,
                    },
                    {
                        // editace další osoby složky - bez id v cestě by se vždy editoval vlastník složky
                        path: 'edit/:personId',
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderEdit,
                                alias: BreadcrumbsEnum.FolderEdit,
                            },
                        },
                        title: xCarolName,
                        component: UpsertPersonPageComponent,
                    },
                    {
                        path: 'add-person',
                        title: xCarolName,
                        data: {
                            // režim "přidat další osobu do složky" - viz UpsertPersonPageComponent.folderPersonCreate
                            folderPersonCreate: true,
                            visit: { skip: true },
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderAddPerson,
                                alias: BreadcrumbsEnum.FolderAddPerson,
                            },
                        },
                        component: UpsertPersonPageComponent,
                    },
                    {
                        path: 'dashboard',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.FOLDER_NOTE,
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderDashboard,
                                alias: BreadcrumbsEnum.FolderDashboard,
                            },
                        },
                        component: FolderDashboardPageComponent,
                    },
                    {
                        path: 'contacts',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.PERSON_CONTACT,
                            breadcrumb: {
                                label: BreadcrumbsEnum.PersonContacts,
                                alias: BreadcrumbsEnum.PersonContacts,
                            },
                        },
                        component: PersonContactsPageComponent,
                    },
                    {
                        path: 'anamnesis',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.ANAMNESIS,
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderAnamnesis,
                                alias: BreadcrumbsEnum.FolderAnamnesis,
                            },
                        },
                        component: FolderAnamnesisPageComponent,
                    },
                    {
                        path: 'epicrisis',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.EPICRISIS,
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderEpicrisis,
                                alias: BreadcrumbsEnum.FolderEpicrisis,
                            },
                        },
                        component: FolderEpicrisisPageComponent,
                    },
                    {
                        path: 'care-plan',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.CARE_PLAN,
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderCarePlans,
                                alias: BreadcrumbsEnum.FolderCarePlans,
                            },
                        },
                        component: FolderCarePlanPageComponent,
                    },
                    {
                        path: 'medication',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.FOLDER_MEDICATION,
                            breadcrumb: {
                                alias: BreadcrumbsEnum.FolderMedications,
                            },
                        },
                        component: FolderMedicationsPageComponent,
                    },
                    {
                        path: 'eprescriptions',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                alias: BreadcrumbsEnum.FolderEPrescriptions,
                            },
                        },
                        component: FolderEPrescriptionsPageComponent,
                    },
                    {
                        path: 'eprescriptions',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderEPrescriptions,
                                alias: BreadcrumbsEnum.FolderEPrescriptions,
                            },
                        },
                        children: [
                            {
                                path: 'create',
                                title: xCarolName,
                                data: {
                                    breadcrumb: {
                                        label: BreadcrumbsEnum.FolderEPrescriptionCreate,
                                        alias: BreadcrumbsEnum.FolderEPrescriptionCreate,
                                    },
                                },
                                component: FolderEPrescriptionCreateComponent,
                            },
                            {
                                path: ':prescriptionId',
                                title: xCarolName,
                                data: {
                                    breadcrumb: {
                                        label: BreadcrumbsEnum.FolderEPrescription,
                                        alias: BreadcrumbsEnum.FolderEPrescription,
                                    },
                                },
                                component: FolderEPrescriptionDetailComponent,
                            },
                        ],
                    },
                    {
                        path: 'service-record',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WORK,
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderServiceRecord,
                                alias: BreadcrumbsEnum.FolderServiceRecord,
                            },
                        },
                        component: FolderServiceRecordsPageComponent,
                    },
                    {
                        path: 'vouchers',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.VOUCHER,
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderVouchers,
                                alias: BreadcrumbsEnum.FolderVouchers,
                            },
                        },
                        component: VouchersPageComponent,
                    },
                    {
                        path: 'vouchers',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderVouchers,
                                alias: BreadcrumbsEnum.FolderVouchers,
                            },
                        },
                        children: [
                            {
                                path: ':voucherId',
                                title: xCarolName,
                                data: {
                                    breadcrumb: {
                                        label: BreadcrumbsEnum.FolderVoucher,
                                        alias: BreadcrumbsEnum.FolderVoucher,
                                    },
                                },
                                component: VoucherDetailPageComponent,
                            },
                            {
                                path: ':voucherId',
                                title: xCarolName,
                                data: {
                                    breadcrumb: {
                                        label: BreadcrumbsEnum.FolderVoucher,
                                        alias: BreadcrumbsEnum.FolderVoucher,
                                    },
                                },
                                children: [
                                    {
                                        path: 'plan',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: {
                                                label: BreadcrumbsEnum.FolderVoucherNursingCarePlan,
                                                alias: BreadcrumbsEnum.FolderVoucherNursingCarePlan,
                                            },
                                        },
                                        component:
                                        VoucherNursingCarePlanDetailPageComponent,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: 'nurse-records',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WORK,
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderNurseRecords,
                                alias: BreadcrumbsEnum.FolderNurseRecords,
                            },
                        },
                        component: FolderNurseRecordsPageComponent,
                    },
                    {
                        path: 'unrealized-visits',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WORK,
                            breadcrumb: {
                                label: BreadcrumbsEnum.WorkUnrealizedVisits,
                                alias: BreadcrumbsEnum.WorkUnrealizedVisits,
                            },
                        },
                        component: FolderUnrealizedVisitPageComponent,
                    },
                    {
                        path: 'extramural-care',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WORK,
                            breadcrumb: {
                                label: BreadcrumbsEnum.ExtramuralCare,
                                alias: BreadcrumbsEnum.ExtramuralCare,
                            },
                        },
                        component: FolderExtramuralCarePageComponent,
                    },
                    {
                        path: 'doctor-records',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WORK,
                            breadcrumb: {
                                label: BreadcrumbsEnum.DoctorRecords,
                                alias: BreadcrumbsEnum.DoctorRecords,
                            },
                        },
                        component: FolderDoctorRecordsPageComponent,
                    },
                    {
                        path: 'medical-reports',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.FOLDER_MEDICAL_REPORT,
                            breadcrumb: {
                                label: BreadcrumbsEnum.MedicalReports,
                                alias: BreadcrumbsEnum.MedicalReports,
                            },
                        },
                        component: FolderMedicalReportPageComponent,
                    },
                    {
                        path: 'specialist-records',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WORK,
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderSpecialistRecords,
                                alias: BreadcrumbsEnum.FolderSpecialistRecords,
                            },
                        },
                        component: FolderSpecialistRecordsPageComponent,
                    },
                    {
                        path: 'documents',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.FILE,
                            breadcrumb: {
                                label: BreadcrumbsEnum.Documents,
                                alias: BreadcrumbsEnum.Documents,
                            },
                        },
                        component: FolderDocumentsPageComponent,
                    },
                    {
                        path: 'forms',
                        title: xCarolName,
                        data: {
                            trashEntityName: [
                                TrashEntityTypeEnum.FOLDER_FORM,
                                TrashEntityTypeEnum.FOLDER_FORM_DATA,
                            ],
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderForms,
                                alias: BreadcrumbsEnum.FolderForms,
                            },
                        },
                        component: FolderFormsPageComponent,
                    },
                    {
                        path: 'sick-notes',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderSickNotes,
                                alias: BreadcrumbsEnum.FolderSickNotes,
                            },
                        },
                        component: FolderSickNotesPageComponent,
                    },
                    {
                        path: 'sick-notes',
                        title: xCarolName,
                        data: {
                            breadcrumb: BreadcrumbsEnum.FolderSickNotes,
                        },
                        children: [
                            {
                                path: 'create',
                                title: xCarolName,
                                data: {
                                    breadcrumb:
                                    BreadcrumbsEnum.FolderSickNotesCreate,
                                },
                                component: FolderSickNotesDetailPageComponent,
                            },
                            {
                                path: ':sickNoteId',
                                title: xCarolName,
                                data: {
                                    breadcrumb: {
                                        label: BreadcrumbsEnum.FolderSickNote,
                                        alias: BreadcrumbsEnum.FolderSickNote,
                                    },
                                },
                                component: FolderSickNotesDetailPageComponent,
                            },
                        ],
                    },
                    {
                        path: 'individual-plan',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.FOLDER_INDIVIDUAL_PLAN,
                            breadcrumb: BreadcrumbsEnum.IndividualPlans,
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/folder/individual-plan/individual-plan.component'
                                ).then((m) => m.IndividualPlanComponent),
                    },
                    {
                        path: 'contracts',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.FOLDER_CONTRACT,
                            breadcrumb: BreadcrumbsEnum.Contracts,
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/folder/contracts/contracts-entry.component'
                                ).then((m) => m.ContractsEntryComponent),
                        children: [
                            {
                                path: '',
                                title: xCarolName,
                                canActivate: [ContractsGuard],
                                loadComponent: () =>
                                    import(
                                        '../../components/social-service/folder/contracts/contracts.component'
                                        ).then((m) => m.ContractsComponent),
                            },
                            {
                                path: 'create',
                                title: xCarolName,
                                data: {
                                    breadcrumb: BreadcrumbsEnum.ContractCreate,
                                },
                                loadComponent: () =>
                                    import(
                                        '../../components/social-service/folder/contracts/contract-detail/contract-detail.component'
                                        ).then((m) => m.ContractDetailComponent),
                            },
                            {
                                path: ':contractId',
                                title: xCarolName,
                                data: {
                                    breadcrumb: {
                                        label: BreadcrumbsEnum.N,
                                        alias: BreadcrumbsEnum.N,
                                    },
                                },
                                loadComponent: () =>
                                    import(
                                        '../../components/social-service/folder/contracts/contract-detail/contract-detail.component'
                                        ).then((m) => m.ContractDetailComponent),
                            },
                        ],
                    },
                    {
                        path: 'attendance',
                        title: xCarolName,
                        data: {
                            breadcrumb: BreadcrumbsEnum.Attendance,
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/attendance/folder-attendances/folder-attendances.component'
                                ).then((m) => m.FolderAttendancesComponent),
                    },
                    {
                        path: 'continuous-record',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WORK,
                            breadcrumb: {
                                label: BreadcrumbsEnum.WorkContinuousRecords,
                                alias: BreadcrumbsEnum.WorkContinuousRecords,
                            },
                        },
                        component: FolderContinuousRecordComponent,
                    },
                    {
                        path: 'movement-restriction',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WORK,
                            breadcrumb: {
                                label: BreadcrumbsEnum.WorkMovementRestriction,
                                alias: BreadcrumbsEnum.WorkMovementRestriction,
                            },
                        },
                        component: WorkMovementRestrictionsPageComponent,
                    },
                    {
                        path: 'sanction',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WORK,
                            breadcrumb: {
                                label: BreadcrumbsEnum.WorkSanction,
                                alias: BreadcrumbsEnum.WorkSanction,
                            },
                        },
                        component: WorkSanctionsPageComponent,
                    },
                    {
                        path: 'suggestion',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WORK,
                            breadcrumb: {
                                label: BreadcrumbsEnum.Suggestion,
                                alias: BreadcrumbsEnum.Suggestion,
                            },
                        },
                        component: FolderSuggestionsPageComponent,
                    },
                    {
                        path: 'socialWorks',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WORK,
                            breadcrumb: {
                                label: BreadcrumbsEnum.SocialWorks,
                                alias: BreadcrumbsEnum.SocialWorks,
                            },
                        },
                        component: FolderWorksPageComponent,
                    },
                    {
                        path: 'tangible-deposits',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WORK,
                            breadcrumb: {
                                label: BreadcrumbsEnum.WorkTangibleDeposits,
                                alias: BreadcrumbsEnum.WorkTangibleDeposits,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/folder/tangible-deposits/folder-tangible-deposits-page.component'
                                ).then((m) => m.FolderTangibleDepositsPageComponent),
                    },
                    {
                        path: 'social-financial-deposit',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.SOCIAL_FINANCIAL_DEPOSIT,
                            breadcrumb: BreadcrumbsEnum.SocialFinancialDeposit,
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/billing/social-financial-deposit/social-financial-deposit.component'
                                ).then((m) => m.SocialFinancialDepositComponent),
                    },
                    {
                        path: 'social-folder-billing',
                        title: xCarolName,
                        data: {
                            trashEntityName: [
                                TrashEntityTypeEnum.SOCIAL_BILLING_DOCUMENT,
                                TrashEntityTypeEnum.SOCIAL_BILLING_RECEIPT,
                                TrashEntityTypeEnum.SOCIAL_PAYMENT,
                                TrashEntityTypeEnum.CARE_PAYMENT,
                            ],
                            breadcrumb: BreadcrumbsEnum.SocialFolderBilling,
                        },
                        children: [
                            {
                                path: '',
                                title: xCarolName,
                                loadComponent: () =>
                                    import(
                                        '../../components/social-service/billing/social-folder-billing/social-folder-billing.component'
                                        ).then((m) => m.SocialFolderBillingComponent),
                            },
                            {
                                path: ':billingDocumentId',
                                title: xCarolName,
                                data: {
                                    breadcrumb: BreadcrumbsEnum.SocialBillingDetail,
                                },
                                loadComponent: () =>
                                    import(
                                        '../../components/social-service/billing/social-billing-detail-page/social-billing-detail-page.component'
                                        ).then((m) => m.SocialBillingDetailPageComponent),
                            },
                        ],
                    },
                    {
                        path: 'operational-report',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WORK,
                            breadcrumb: {
                                label: BreadcrumbsEnum.WorkOperationalReports,
                                alias: BreadcrumbsEnum.WorkOperationalReports,
                            },
                        },
                        component: FolderOperationalReportPageComponent,
                    },
                    {
                        path: 'mail',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.SERVICE_MAIL,
                            breadcrumb: {
                                label: BreadcrumbsEnum.Mail,
                                alias: BreadcrumbsEnum.Mail,
                            },
                        },
                        loadComponent: () => import('./pages/person/folder-service-mail-records/folder-service-mail-records-page/folder-service-mail-records-page.component').then((m) => m.FolderServiceMailRecordsPageComponent),
                    },
                    {
                        path: 'tasks',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.USER_TASK,
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderTasks,
                                alias: BreadcrumbsEnum.FolderTasks,
                            },
                        },
                        loadComponent: () => import('../../components/social-service/user/user-tasks/folder-user-tasks/folder-user-tasks.component').then((m) => m.FolderUserTasksComponent),
                    },
                    {
                        path: 'warehouse-material',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WAREHOUSE_MATERIAL_OPERATION,
                            breadcrumb: BreadcrumbsEnum.WarehouseMaterial,
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/folder/warehouse-material/warehouse-material.component'
                                ).then((m) => m.WarehouseMaterialComponent),
                    },
                    {
                        path: 'service-announcements',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.WORK,
                            breadcrumb: BreadcrumbsEnum.ServiceAnnouncements,
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/folder/service-announcements/service-announcements-page/service-announcements-page.component'
                                ).then((m) => m.ServiceAnnouncementsPageComponent),
                    },
                    {
                        path: 'epoukaz',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                alias: BreadcrumbsEnum.FolderEPoukaz,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/medical-service/folder/folder-epoukaz/folder-epoukaz-list-page/folder-epoukaz-list-page.component'
                                ).then((m) => m.FolderEPoukazListPageComponent),
                    },
                    {
                        path: 'epoukaz',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.FolderEPoukaz,
                                alias: BreadcrumbsEnum.FolderEPoukaz,
                            },
                        },
                        children: [
                            {
                                path: 'create',
                                title: xCarolName,
                                data: {
                                    breadcrumb: {
                                        label: BreadcrumbsEnum.FolderEPoukazCreate,
                                        alias: BreadcrumbsEnum.FolderEPoukazCreate,
                                    },
                                },
                                loadComponent: () =>
                                    import(
                                        '../../components/medical-service/folder/folder-epoukaz/folder-epoukaz-create/folder-epoukaz-create.component'
                                        ).then((m) => m.FolderEPoukazCreateComponent),
                            },
                            {
                                path: ':ePoukazId',
                                title: xCarolName,
                                data: { breadcrumb: BreadcrumbsEnum.FolderEPoukazDetail },
                                loadComponent: () => import(
                                    '../../components/medical-service/folder/folder-epoukaz/folder-epoukaz-detail/folder-epoukaz-detail.component'
                                    ).then((m) => m.FolderEPoukazDetailComponent),
                            },
                        ],
                    },
                    {
                        path: 'nursing-allowance',
                        title: generalName,
                        canMatch: [() => Boolean(featureOn.NURSING_ALLOWANCE)],
                        data: { breadcrumb: BreadcrumbsEnum.FolderNursingAllowance },
                        loadComponent: () => import(
                            '../../components/medical-service/folder/folder-nursing-allowance/folder-nursing-allowance-page/folder-nursing-allowance-page.component'
                            ).then(m => m.FolderNursingAllowancePageComponent),
                    },
                    {
                        path: 'nursing-allowance',
                        title: generalName,
                        canMatch: [() => Boolean(featureOn.NURSING_ALLOWANCE)],
                        data: { breadcrumb: BreadcrumbsEnum.FolderNursingAllowance },
                        children: [
                            {
                                path: 'create',
                                data: { breadcrumb: BreadcrumbsEnum.FolderNursingAllowanceCreate },
                                loadComponent: () => import(
                                    '../../components/medical-service/folder/folder-nursing-allowance/folder-nursing-allowance-form-page/folder-nursing-allowance-form-page.component'
                                    ).then(m => m.FolderNursingAllowanceFormPageComponent),
                            },
                            {
                                path: ':nursingAllowanceId',
                                data: { breadcrumb: BreadcrumbsEnum.FolderNursingAllowanceDetail },
                                loadComponent: () => import(
                                    '../../components/medical-service/folder/folder-nursing-allowance/folder-nursing-allowance-form-page/folder-nursing-allowance-form-page.component'
                                    ).then(m => m.FolderNursingAllowanceFormPageComponent),
                            },
                        ],
                    },
                    {
                        path: 'social-credit',
                        title: generalName,
                        canMatch: [() => Boolean(featureOn.SOCIAL_CREDITS)],
                        data: { breadcrumb: BreadcrumbsEnum.SocialCredit },
                        loadComponent: () => import(
                            '../../components/social-service/folder/social-credit/social-credit.component'
                        ).then(m => m.SocialCreditComponent),
                    },
                    {
                        path: 'ereferrals',
                        title: generalName,
                        canMatch: [() => Boolean(featureOn.EREFERRALS)],
                        data: { breadcrumb: BreadcrumbsEnum.FolderEReferrals },
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                loadComponent: () => import(
                                    '../../components/medical-service/folder/folder-ereferrals/folder-ereferral-list-page/folder-ereferral-list-page.component'
                                ).then(m => m.FolderEReferralListPageComponent),
                            },
                            {
                                path: 'create',
                                data: { breadcrumb: BreadcrumbsEnum.FolderEReferralCreate },
                                loadComponent: () => import(
                                    '../../components/medical-service/folder/folder-ereferrals/folder-ereferral-form/folder-ereferral-form.component'
                                ).then(m => m.FolderEReferralFormComponent),
                            },
                            {
                                path: ':eReferralId',
                                data: { breadcrumb: BreadcrumbsEnum.FolderEReferralDetail },
                                loadComponent: () => import(
                                    '../../components/medical-service/folder/folder-ereferrals/folder-ereferral-form/folder-ereferral-form.component'
                                ).then(m => m.FolderEReferralFormComponent),
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        path: 'files',
        title: generalName,
        data: {
            trashEntityName: TrashEntityTypeEnum.FILE,
            breadcrumb: {
                label: BreadcrumbsEnum.FilesManager,
                alias: BreadcrumbsEnum.FilesManager,
            },
        },
        loadComponent: () =>
            import(
                './pages/services/files-manager-page/files-manager-page.component'
                ).then((m) => m.FilesManagerPageComponent),
    },
];

const socialRoutes: Routes = [
    {
        // Overview page
        path: 'works',
        title: xCarolName,
        data: {
            trashEntityName: TrashEntityTypeEnum.WORK,
            visit: { skip: true },
            breadcrumb: BreadcrumbsEnum.Works,
        },
        loadComponent: () =>
            import('./pages/services/works-page/works-page.component').then(
                (m) => m.WorksPageComponent,
            ),
        children: [
            {
                path: 'operational-report',
                title: xCarolName,
                canDeactivate: [unsavedChangesGuard],
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkOperationalReport,
                },
                loadComponent: () =>
                    import(
                        '../../components/common-service/work/pages/work-operational-report-page/work-operational-report-page.component'
                        ).then((m) => m.WorkOperationalReportPageComponent),
                children: [
                    {
                        path: ':workId',
                        title: xCarolName,
                        canDeactivate: [unsavedChangesGuard],
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/common-service/work/pages/work-operational-report-page/work-operational-report-page.component'
                                ).then((m) => m.WorkOperationalReportPageComponent),
                    },
                ],
            },
            {
                path: 'operational-work',
                title: xCarolName,
                canMatch: [() => Boolean(featureOn.NEW_CALENDAR)],
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkOperationalWork,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/work-operational-work/work-operational-work-page/work-operational-work-page.component'
                        ).then((m) => m.WorkOperationalWorkPageComponent),
                children: [
                    {
                        path: ':workId',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/work/work-operational-work/work-operational-work-page/work-operational-work-page.component'
                                ).then((m) => m.WorkOperationalWorkPageComponent),
                    },
                ],
            },
            {
                path: 'movement-restriction',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkMovementRestriction,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/pages/work-movement-restriction-page/work-movement-restriction-page.component'
                        ).then((m) => m.WorkMovementRestrictionPageComponent),
                children: [
                    {
                        path: ':workId',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/work/pages/work-movement-restriction-page/work-movement-restriction-page.component'
                                ).then(
                                (m) => m.WorkMovementRestrictionPageComponent,
                            ),
                    },
                ],
            },
            {
                path: 'sanction',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkSanction,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/pages/work-sanction-page/work-sanction-page.component'
                        ).then((m) => m.WorkSanctionPageComponent),
                children: [
                    {
                        path: ':workId',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/work/pages/work-sanction-page/work-sanction-page.component'
                                ).then((m) => m.WorkSanctionPageComponent),
                    },
                ],
            },
            {
                path: 'satisfaction',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkSuggestionSatisfaction,
                    suggestionType: WorkDataSuggestionTypeEnum.satisfaction,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/pages/work-suggestion-page/work-suggestion-page.component'
                        ).then((m) => m.WorkSuggestionPageComponent),
                children: [
                    {
                        path: ':workId',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/work/pages/work-suggestion-page/work-suggestion-page.component'
                                ).then((m) => m.WorkSuggestionPageComponent),
                    },
                ],
            },
            {
                path: 'complaint',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkSuggestionComplaint,
                    suggestionType: WorkDataSuggestionTypeEnum.complaint,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/pages/work-suggestion-page/work-suggestion-page.component'
                        ).then((m) => m.WorkSuggestionPageComponent),
                children: [
                    {
                        path: ':workId',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/work/pages/work-suggestion-page/work-suggestion-page.component'
                                ).then((m) => m.WorkSuggestionPageComponent),
                    },
                ],
            },
            {
                path: 'finding',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkSuggestionFinding,
                    suggestionType: WorkDataSuggestionTypeEnum.finding,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/pages/work-suggestion-page/work-suggestion-page.component'
                        ).then((m) => m.WorkSuggestionPageComponent),
                children: [
                    {
                        path: ':workId',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/work/pages/work-suggestion-page/work-suggestion-page.component'
                                ).then((m) => m.WorkSuggestionPageComponent),
                    },
                ],
            },
            {
                path: 'individual-work',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkIndividualWork,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/work-individual-work/work-individual-work-page/work-individual-work-page.component'
                        ).then((m) => m.WorkIndividualWorkPageComponent),
                children: [
                    {
                        path: ':workId',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/work/work-individual-work/work-individual-work-page/work-individual-work-page.component'
                                ).then((m) => m.WorkIndividualWorkPageComponent),
                    },
                ],
            },
            {
                path: 'tangible-deposit',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkTangibleDeposit,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/work-tangible-deposit/work-tangible-deposit-page/work-tangible-deposit-page.component'
                        ).then((m) => m.WorkTangibleDepositPageComponent),
                children: [
                    {
                        path: ':workId',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/work/work-tangible-deposit/work-tangible-deposit-page/work-tangible-deposit-page.component'
                                ).then((m) => m.WorkTangibleDepositPageComponent),
                    },
                ],
            },
            {
                path: 'group-activity',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkGroupActivity,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/work-group-activity/work-group-activity-page/work-group-activity-page.component'
                        ).then((m) => m.WorkGroupActivityPageComponent),
                children: [
                    {
                        path: ':workId',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/work/work-group-activity/work-group-activity-page/work-group-activity-page.component'
                                ).then((m) => m.WorkGroupActivityPageComponent),
                    },
                ],
            },
            {
                path: 'mass-work',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkMassWork,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/work-mass-work/work-mass-work-page/work-mass-work-page.component'
                        ).then((m) => m.WorkMassWorkPageComponent),
                children: [
                    {
                        path: ':workId',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/work/work-mass-work/work-mass-work-page/work-mass-work-page.component'
                                ).then((m) => m.WorkMassWorkPageComponent),
                    },
                ],
            },
            {
                path: 'continuous-record',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkContinuousRecord,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/work-continuous-record/work-continuous-record-page/work-continuous-record-page.component'
                        ).then((m) => m.WorkContinuousRecordPageComponent),
                children: [
                    {
                        path: ':workId',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/social-service/work/work-continuous-record/work-continuous-record-page/work-continuous-record-page.component'
                                ).then((m) => m.WorkContinuousRecordPageComponent),
                    },
                ],
            },
        ],
    },
    {
        path: 'realized-work',
        title: xCarolName,
        data: {
            trashEntityName: TrashEntityTypeEnum.WORK,
            breadcrumb: BreadcrumbsEnum.RealizedWorks,
        },
        children: [
            {
                path: 'list/operational-reports',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkOperationalReports,
                },
                loadComponent: () =>
                    import(
                        '../../components/common-service/work/pages/work-operational-reports-page/work-operational-reports-page.component'
                        ).then((m) => m.WorkOperationalReportsPageComponent),
            },
            {
                path: 'list/operational-work',
                title: xCarolName,
                canMatch: [() => Boolean(featureOn.NEW_CALENDAR)],
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkOperationalWorks,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/work-operational-works-table/work-operational-works-page/work-operational-works-page.component'
                        ).then((m) => m.WorkOperationalWorksPageComponent),
            },
            {
                path: 'list/movement-restriction',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkMovementRestriction,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/pages/work-movement-restrictions-page/work-movement-restrictions-page.component'
                        ).then((m) => m.WorkMovementRestrictionsPageComponent),
            },
            {
                path: 'list/sanction',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkSanction,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/pages/work-sanctions-page/work-sanctions-page.component'
                        ).then((m) => m.WorkSanctionsPageComponent),
            },
            {
                path: 'list/satisfaction',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkSuggestionSatisfaction,
                    suggestionType: 'satisfaction',
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/pages/work-suggestions-page/work-suggestions-page.component'
                        ).then((m) => m.WorkSuggestionsPageComponent),
            },
            {
                path: 'list/complaint',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkSuggestionComplaint,
                    suggestionType: 'complaint',
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/pages/work-suggestions-page/work-suggestions-page.component'
                        ).then((m) => m.WorkSuggestionsPageComponent),
            },
            {
                path: 'list/finding',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkSuggestionFinding,
                    suggestionType: 'finding',
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/pages/work-suggestions-page/work-suggestions-page.component'
                        ).then((m) => m.WorkSuggestionsPageComponent),
            },
            {
                path: 'list/individual-work',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.WorkIndividualWork },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/work-individual-works-table/work-individual-works-page/work-individual-works-page.component'
                        ).then((m) => m.WorkIndividualWorksPageComponent),
            },
            {
                path: 'list/continuous-record',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.WorkContinuousRecords },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/work-continuous-records-table/work-continuous-records-page/work-continuous-records-page.component'
                        ).then((m) => m.WorkContinuousRecordsPageComponent),
            },
            {
                path: 'list/service-announcements',
                title: xCarolName,
                data: {
                    trashEntityName: TrashEntityTypeEnum.WORK,
                    breadcrumb: BreadcrumbsEnum.ServiceAnnouncements,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/work-service-announcements/work-service-announcements-page/work-service-announcements-page.component'
                        ).then((m) => m.WorkServiceAnnouncementsPageComponent),
            },
            {
                path: 'list/tangible-deposit',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.WorkTangibleDeposits },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/work-tangible-deposits-table/work-tangible-deposits-page/work-tangible-deposits-page.component'
                        ).then((m) => m.WorkTangibleDepositsPageComponent),
            },
            {
                path: 'list/group-activity',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.WorkGroupActivity },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/work-group-activities-table/work-group-activities-page/work-group-activities-page.component'
                        ).then((m) => m.WorkGroupActivitiesPageComponent),
            },
            {
                path: 'list/workers-work',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkWorkersWork,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/work-workers-works/work-workers-works-page/work-workers-works-page.component'
                        ).then((m) => m.WorkWorkersWorksPageComponent),
            },

        ],
    },
    {
        path: 'calendar',
        title: xCarolName,
        canActivate: [planningCalendarGuard],
        data: {
            trashEntityName: TrashEntityTypeEnum.CALENDAR_ITEM,
            breadcrumb: {
                label: BreadcrumbsEnum.Scheduler,
                alias: BreadcrumbsEnum.Scheduler,
            },
        },
        loadComponent: () =>
            import(
                '../../components/social-service/calendar/social-calendar.component'
                ).then((m) => m.SocialCalendarComponent),
    },
    {
        path: 'billing',
        title: xCarolName,
        data: {
            breadcrumb: {
                label: BreadcrumbsEnum.Billing,
                disable: true,
            },
        },
        children: [
            {
                path: '',
                pathMatch: 'full',
                title: xCarolName,
                redirectTo: 'batch',
            },
            {
                path: 'batch',
                title: xCarolName,
                data: {
                    trashEntityName: [
                        TrashEntityTypeEnum.SOCIAL_BILLING_DOCUMENT,
                        TrashEntityTypeEnum.SOCIAL_BILLING_RECEIPT,
                        TrashEntityTypeEnum.SOCIAL_PAYMENT,
                    ],
                    breadcrumb: BreadcrumbsEnum.SocialBilling,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/billing/social-service-billings/social-service-billings.component'
                        ).then((m) => m.SocialServiceBillingsComponent),
            },
            {
                path: 'batch-advance',
                title: xCarolName,
                data: {
                    trashEntityName: [
                        TrashEntityTypeEnum.SOCIAL_BILLING_DOCUMENT,
                        TrashEntityTypeEnum.SOCIAL_BILLING_RECEIPT,
                        TrashEntityTypeEnum.SOCIAL_PAYMENT,
                    ],
                    breadcrumb: BreadcrumbsEnum.SocialBillingAdvance,
                    advance: true,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/billing/social-service-billings/social-service-billings.component'
                        ).then((m) => m.SocialServiceBillingsComponent),
            },
            {
                path: 'documents',
                title: xCarolName,
                data: {
                    trashEntityName: [
                        TrashEntityTypeEnum.SOCIAL_BILLING_DOCUMENT,
                        TrashEntityTypeEnum.SOCIAL_BILLING_RECEIPT,
                        TrashEntityTypeEnum.SOCIAL_PAYMENT,
                    ],
                    breadcrumb: BreadcrumbsEnum.SocialBillingDocuments,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/billing/social-service-documents/social-service-documents.component'
                        ).then((m) => m.SocialServiceDocumentsComponent),
            },
            {
                path: 'unbilled-work',
                title: xCarolName,
                data: {
                    trashEntityName: TrashEntityTypeEnum.WORK,
                    breadcrumb: BreadcrumbsEnum.BillingUnbilledWork,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/billing/social-service-unbilled-works/social-service-unbilled-works.component'
                        ).then((m) => m.SocialServiceUnbilledWorksComponent),
            },
        ],
    },
    {
        path: 'basic-social-consulting',
        title: xCarolName,
        data: {
            trashEntityName: TrashEntityTypeEnum.BASIC_SOCIAL_CONSULTING,
            breadcrumb: BreadcrumbsEnum.BasicSocialConsulting,
        },
        loadComponent: () =>
            import('../../components/social-service/basic-social-consulting/basic-social-consulting.component')
                .then(m => m.BasicSocialConsultingComponent),
    },
    {
        path: 'diet',
        title: xCarolName,
        data: {
            trashEntityName: TrashEntityTypeEnum.DIET_ORDER,
            breadcrumb: BreadcrumbsEnum.ServiceDietOrder,
        },
        loadComponent: () => import('../../components/social-service/diet-order/diet-order.component').then(m => m.DietOrderComponent),
    },
    {
        path: 'service-messages',
        title: xCarolName,
        data: {
            trashEntityName: TrashEntityTypeEnum.MESSAGE,
            breadcrumb: BreadcrumbsEnum.ServiceMessages,
        },
        loadComponent: () =>
            import('../../components/social-service/service-messages/service-messages.component')
                .then(m => m.ServiceMessagesComponent),
    },
    {
        path: 'service-messages',
        title: xCarolName,
        data: {
            trashEntityName: TrashEntityTypeEnum.MESSAGE,
            breadcrumb: BreadcrumbsEnum.ServiceMessages,
        },
        children: [
            {
                path: 'create',
                title: xCarolName,
                loadComponent: () =>
                    import(
                        '../../components/system-setting/message/service-message-detail-page/service-message-detail-page.component'
                        ).then((m) => m.ServiceMessageDetailPageComponent),
            },
            {
                path: ':id',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MessageTitle,
                        alias: BreadcrumbsEnum.MessageTitle,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/system-setting/message/service-message-detail-page/service-message-detail-page.component'
                        ).then((m) => m.ServiceMessageDetailPageComponent),
            },
        ],
    },
    {
        path: 'waiting-list',
        title: xCarolName,
        data: {
            trashEntityName: [
                TrashEntityTypeEnum.WAITING_LIST,
                TrashEntityTypeEnum.WAITING_LIST_RATING,
            ],
            breadcrumb: BreadcrumbsEnum.WaitingList,
        },
        loadComponent: () => import('../../components/social-service/waiting-list/waiting-list/waiting-list.component').then(m => m.WaitingListComponent),
    },
    {
        path: 'tools',
        title: xCarolName,
        data: { breadcrumb: BreadcrumbsEnum.Tools, disable: true },
        children: [
            {
                path: 'provided-operations-reports',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.ProvidedOperationsReports,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/provided-operations-reports/provided-operations-reports.component'
                        ).then((m) => m.ProvidedOperationsReportsComponent),
            },
            {
                path: 'pensions-income-tool',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.PensionsIncomeTool,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/tools/pensions-income-tool/pensions-income-tool.component'
                        ).then((m) => m.PensionsIncomeToolComponent),
            },
            {
                path: 'contractual-surcharge-income-tool',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.ContractualSurchargeIncomeTool,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/tools/contractual-surcharge-income-tool/contractual-surcharge-income-tool.component'
                        ).then((m) => m.ContractualSurchargeIncomeToolComponent),
            },
            {
                path: 'social-contribution-income-tool',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.SocialContributionIncomeTool,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/tools/social-contribution-income-tool/social-contribution-income-tool.component'
                        ).then((m) => m.SocialContributionIncomeToolComponent),
            },
            {
                path: 'financial-deposit-bulk-tool',
                title: xCarolName,
                data: {
                    trashEntityName: TrashEntityTypeEnum.SOCIAL_FINANCIAL_DEPOSIT,
                    breadcrumb: BreadcrumbsEnum.FinancialDepositBulkTool,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/tools/financial-deposit-bulk-tool/financial-deposit-bulk-tool.component'
                        ).then((m) => m.FinancialDepositBulkToolComponent),
            },
            {
                path: 'deficit-tool',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.DeficitTool,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/tools/deficit-tool/deficit-tool.component'
                        ).then((m) => m.DeficitToolComponent),
            },
            {
                path: 'financial-movements-tool',
                title: xCarolName,
                data: {
                    trashEntityName: [
                        TrashEntityTypeEnum.SOCIAL_FINANCIAL_DEPOSIT,
                        TrashEntityTypeEnum.SOCIAL_PAYMENT,
                    ],
                    breadcrumb: BreadcrumbsEnum.FinancialMovementsTool,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/tools/financial-movements-tool/financial-movements-tool.component'
                        ).then((m) => m.FinancialMovementsToolComponent),
            },
            {
                path: 'care-contribution-refund-tool',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.CareContributionRefundTool,
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/tools/care-contribution-refund-tool/care-contribution-refund-tool.component'
                        ).then((m) => m.CareContributionRefundToolComponent),
            },
        ],
    },
    {
        path: 'works/service-announcement',
        title: xCarolName,
        data: {
            breadcrumb: {
                label: BreadcrumbsEnum.ServiceAnnouncements,
                routeInterceptor: () => '/content/social/works',
            },
        },
        loadComponent: () =>
            import(
                '../../components/social-service/work/pages/work-service-announcement-page/work-service-announcement-page.component'
                ).then((m) => m.WorkServiceAnnouncementPageComponent),
        children: [
            {
                path: ':workId',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.ServiceAnnouncements,
                        routeInterceptor: () => '/content/social/works',
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/work/pages/work-service-announcement-page/work-service-announcement-page.component'
                        ).then((m) => m.WorkServiceAnnouncementPageComponent),
            },
        ],
    },
    {
        path: 'statistics',
        title: generalName,
        data: {
            breadcrumb: {
                label: BreadcrumbsEnum.Statistics,
                alias: BreadcrumbsEnum.Statistics,
            },
        },
        children: [
            {
                path: SocialStatisticsTypeEnum.WORKERS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsWorkers,
                        alias: BreadcrumbsEnum.SocialServiceStatisticsWorkers,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-workers/social-service-statistics-workers.component'
                        ).then((m) => m.SocialServiceStatisticsWorkersComponent),
            },
            {
                path: SocialStatisticsTypeEnum.OPERATIONAL_REPORT,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsOperationalReport,
                        alias: BreadcrumbsEnum.SocialServiceStatisticsOperationalReport,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-operational-report/social-service-statistics-operational-report.component'
                        ).then(
                        (m) =>
                            m.SocialServiceStatisticsOperationalReportComponent,
                    ),
            },
            {
                path: SocialStatisticsTypeEnum.CLIENT_FOLDERS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsFolders,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsFolders,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-client-folders/social-service-statistics-client-folders.component'
                        ).then((m) => m.SocialServiceStatisticsClientFoldersComponent),
            },
            {
                path: SocialStatisticsTypeEnum.DECEASED_CLIENT_CONTACTS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsDeceasedClientsContacts,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsDeceasedClientsContacts,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-deceased-client-contacts/social-service-statistics-deceased-client-contacts.component'
                        ).then((m) => m.SocialServiceStatisticsDeceasedClientContactsComponent),
            },
            {
                path: SocialStatisticsTypeEnum.PERFORMED_WORK_WORKERS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsPerformedWorkWorkers,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-work-workers/social-service-statistics-work-workers.component'
                    ).then((m) => m.SocialServiceStatisticsWorkWorkersComponent),
            },
            {
                path: SocialStatisticsTypeEnum.PERFORMED_WORK_WORKERS_OFFERED_ACTIVITIES,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsPerformedWorkWorkersOfferedActivities,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-work-workers-offered-activities/social-service-statistics-work-workers-offered-activities.component'
                    ).then((m) => m.SocialServiceStatisticsWorkWorkersOfferedActivitiesComponent),
            },
            {
                path: SocialStatisticsTypeEnum.PERFORMED_WORK_INDIVIDUAL_WORKERS_OFFERED_ACTIVITIES,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsPerformedWorkIndividualWorkersOfferedActivities,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-work-individual-workers-offered-activities/social-service-statistics-work-individual-workers-offered-activities.component'
                    ).then((m) => m.SocialServiceStatisticsWorkIndividualWorkersOfferedActivitiesComponent),
            },
            {
                path: SocialStatisticsTypeEnum.PERFORMED_WORK_CLIENTS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsPerformedWorkClients,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-work-clients/social-service-statistics-work-clients.component'
                    ).then((m) => m.SocialServiceStatisticsWorkClientsComponent),
            },
            {
                path: SocialStatisticsTypeEnum.PERFORMED_WORK_CLIENTS_OFFERED_ACTIVITIES,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsPerformedWorkClientsOfferedActivities,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-work-clients-offered-activities/social-service-statistics-work-clients-offered-activities.component'
                    ).then((m) => m.SocialServiceStatisticsWorkClientsOfferedActivitiesComponent),
            },
            {
                path: SocialStatisticsTypeEnum.PERFORMED_WORK_INDIVIDUAL_CLIENTS_OFFERED_ACTIVITIES,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsPerformedWorkIndividualClientsOfferedActivities,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-work-individual-clients-offered-activities/social-service-statistics-work-individual-clients-offered-activities.component'
                    ).then((m) => m.SocialServiceStatisticsWorkIndividualClientsOfferedActivitiesComponent),
            },
            {
                path: SocialStatisticsTypeEnum.PERFORMED_WORK_INDIVIDUAL_CLIENTS_DETAILED_CONTENT,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsPerformedWorkIndividualClientsDetailedContent,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-work-individual-clients-detailed-content/social-service-statistics-work-individual-clients-detailed-content.component'
                    ).then((m) => m.SocialServiceStatisticsWorkIndividualClientsDetailedContentComponent),
            },
            {
                path: SocialStatisticsTypeEnum.PERFORMED_WORK_DAYS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsPerformedWorkDays,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-work-days/social-service-statistics-work-days.component'
                    ).then((m) => m.SocialServiceStatisticsWorkDaysComponent),
            },
            {
                path: SocialStatisticsTypeEnum.PERFORMED_WORK_INTERVENTION_RECORDS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsPerformedWorkInterventionRecords,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-work-intervention-records/social-service-statistics-work-intervention-records.component'
                    ).then((m) => m.SocialServiceStatisticsWorkInterventionRecordsComponent),
            },
            {
                path: SocialStatisticsTypeEnum.PERFORMED_WORK_INDIVIDUAL_WORKER_EDUCATION,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsPerformedWorkIndividualWorkerEducation,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-work-individual-worker-education/social-service-statistics-work-individual-worker-education.component'
                    ).then((m) => m.SocialServiceStatisticsWorkIndividualWorkerEducationComponent),
            },
            {
                path: SocialStatisticsTypeEnum.PERFORMED_WORK_WORKERS_AREA,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsPerformedWorkWorkersArea,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-work-workers-area/social-service-statistics-work-workers-area.component'
                    ).then((m) => m.SocialServiceStatisticsWorkWorkersAreaComponent),
            },
            {
                path: SocialStatisticsTypeEnum.OVERVIEW_SERVICE_PERFORMED_WORK,
                title: xCarolName,
                data: { breadcrumb: { label: BreadcrumbsEnum.Statistics, alias: BreadcrumbsEnum.Statistics } },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-work-summary/social-service-statistics-work-summary.component'
                    ).then((m) => m.SocialServiceStatisticsWorkSummaryComponent),
            },
            {
                path: SocialStatisticsTypeEnum.CLIENT_ATTENDANCE,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsClientAttendance,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-client-attendance/social-service-statistics-client-attendance.component'
                    ).then((m) => m.SocialServiceStatisticsClientAttendanceComponent),
            },
            {
                path: SocialStatisticsTypeEnum.CLIENT_BED_CAPACITY_USAGE,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsClientBedCapacityUsage,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-client-bed-capacity-usage/social-service-statistics-client-bed-capacity-usage.component'
                    ).then((m) => m.SocialServiceStatisticsClientBedCapacityUsageComponent),
            },
            {
                path: SocialStatisticsTypeEnum.MATERIAL_ISSUES,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsMaterialIssues,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-material-issues/social-service-statistics-material-issues.component'
                    ).then((m) => m.SocialServiceStatisticsMaterialIssuesComponent),
            },
            {
                path: SocialStatisticsTypeEnum.DEPENDENCY_LEVELS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsDependencyLevels,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-dependency-levels/social-service-statistics-dependency-levels.component'
                    ).then((m) => m.SocialServiceStatisticsDependencyLevelsComponent),
            },
            {
                path: SocialStatisticsTypeEnum.ORDERED_MEALS_CLIENTS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsOrderedMealsClients,
                        alias: BreadcrumbsEnum.SocialServiceStatisticsOrderedMealsClients,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-ordered-meals-clients/social-service-statistics-ordered-meals-clients.component'
                    ).then((m) => m.SocialServiceStatisticsOrderedMealsClientsComponent),
            },
            {
                path: SocialStatisticsTypeEnum.ORDERED_MEALS_OFFER,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsOrderedMealsOffer,
                        alias: BreadcrumbsEnum.SocialServiceStatisticsOrderedMealsOffer,
                    },
                },
                loadComponent: () => import(
                    '../../components/social-service/statistics/social-service-statistics-ordered-meals-offer/social-service-statistics-ordered-meals-offer.component'
                    ).then((m) => m.SocialServiceStatisticsOrderedMealsOfferComponent),
            },
            {
                path: SocialStatisticsTypeEnum.BILLING,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialBillingDocuments,
                        alias: BreadcrumbsEnum.SocialBillingDocuments,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-billing/social-service-statistics-billing.component'
                        ).then((m) => m.SocialServiceStatisticsBillingComponent),
            },
            {
                path: SocialStatisticsTypeEnum.BILLING_DETAIL,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialBillingDocuments,
                        alias: BreadcrumbsEnum.SocialBillingDocuments,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-billing-detail/social-service-statistics-billing-detail.component'
                        ).then((m) => m.SocialServiceStatisticsBillingDetailComponent),
            },
            {
                path: SocialStatisticsTypeEnum.BILLING_PERMANENT_RESIDENCE_MUNICIPALITY,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialBillingDocuments,
                        alias: BreadcrumbsEnum.SocialBillingDocuments,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-billing-permanent-residence-municipality/social-service-statistics-billing-permanent-residence-municipality.component'
                        ).then((m) => m.SocialServiceStatisticsBillingPermanentResidenceMunicipalityComponent),
            },
            {
                path: SocialStatisticsTypeEnum.BILLING_PERMANENT_RESIDENCE_ORP,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialBillingDocuments,
                        alias: BreadcrumbsEnum.SocialBillingDocuments,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-billing-permanent-residence-orp/social-service-statistics-billing-permanent-residence-orp.component'
                        ).then((m) => m.SocialServiceStatisticsBillingPermanentResidenceOrpComponent),
            },
            {
                path: SocialStatisticsTypeEnum.FINANCIAL_DEPOSIT_CLIENT,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialFinancialDeposit,
                        alias: BreadcrumbsEnum.SocialFinancialDeposit,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-financial-deposit-client/social-service-statistics-financial-deposit-client.component'
                        ).then((m) => m.SocialServiceStatisticsFinancialDepositClientComponent),
            },
            // TODO: no matching the legacy WebCarol calculations. but maybe OK in this
            {
                path: SocialStatisticsTypeEnum.ACHO,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsAcho,
                        alias: BreadcrumbsEnum.SocialServiceStatisticsAcho,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-acho/social-service-statistics-acho.component'
                        ).then((m) => m.SocialServiceStatisticsAchoComponent),
            },
            {
                path: SocialStatisticsTypeEnum.CANCELLATION_ITEMS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsCancellationItems,
                        alias: BreadcrumbsEnum.SocialServiceStatisticsCancellationItems,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-cancellation-items/social-service-statistics-cancellation-items.component'
                        ).then((m) => m.SocialServiceStatisticsCancellationItemsComponent),
            },
            {
                path: SocialStatisticsTypeEnum.MPSV_WORKERS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsMpsvWorkers,
                        alias: BreadcrumbsEnum.SocialServiceStatisticsMpsvWorkers,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-mpsv-workers/social-service-statistics-mpsv-workers.component'
                        ).then((m) => m.SocialServiceStatisticsMpsvWorkersComponent),
            },
            {
                path: SocialStatisticsTypeEnum.MPSV_CLIENTS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsMpsvClients,
                        alias: BreadcrumbsEnum.SocialServiceStatisticsMpsvClients,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-mpsv-clients/social-service-statistics-mpsv-clients.component'
                        ).then((m) => m.SocialServiceStatisticsMpsvClientsComponent),
            },
            {
                path: SocialStatisticsTypeEnum.MPSV_ANNUAL_REPORT,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsMpsvAnnualReport,
                        alias: BreadcrumbsEnum.SocialServiceStatisticsMpsvAnnualReport,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-mpsv-annual-report/social-service-statistics-mpsv-annual-report.component'
                        ).then((m) => m.SocialServiceStatisticsMpsvAnnualReportComponent),
            },
            {
                path: SocialStatisticsTypeEnum.OLOMOUC_REGION,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsOlomoucRegion,
                        alias: BreadcrumbsEnum.SocialServiceStatisticsOlomoucRegion,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-bm-olomouc-region/social-service-statistics-bm-olomouc-region.component'
                        ).then((m) => m.SocialServiceStatisticsBmOlomoucRegionComponent),
            },
            {
                path: SocialStatisticsTypeEnum.ZLIN_REGION,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsZlinRegion,
                        alias: BreadcrumbsEnum.SocialServiceStatisticsZlinRegion,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-bm-zlin-region/social-service-statistics-bm-zlin-region.component'
                        ).then((m) => m.SocialServiceStatisticsBmZlinRegionComponent),
            },
            {
                path: SocialStatisticsTypeEnum.SOUTH_MORAVIAN_REGION,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsSouthMoravianRegion,
                        alias: BreadcrumbsEnum.SocialServiceStatisticsSouthMoravianRegion,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-bm-south-moravian-region/social-service-statistics-bm-south-moravian-region.component'
                        ).then((m) => m.SocialServiceStatisticsBmSouthMoravianRegionComponent),
            },
            {
                path: SocialStatisticsTypeEnum.OVERVIEW_SERVICE_ATTENDANCE,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsOverviewServiceAttendance,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-overview-attendance/social-service-statistics-overview-attendance.component'
                        ).then((m) => m.SocialServiceStatisticsOverviewAttendanceComponent),
            },
            {
                path: SocialStatisticsTypeEnum.OVERVIEW_SERVICE_FOLDER_PERSONS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsOverviewServiceFolderPersons,
                        alias: BreadcrumbsEnum.SocialServiceStatisticsOverviewServiceFolderPersons,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-overview-folder-persons/social-service-statistics-overview-folder-persons.component'
                        ).then(
                        (m) => m.SocialServiceStatisticsOverviewFolderPersonsComponent,
                    ),
            },
            {
                path: SocialStatisticsTypeEnum.OVERVIEW_SERVICE_TARGET_GROUPS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsOverviewServiceTargetGroups,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-overview-target-groups/social-service-statistics-overview-target-groups.component'
                        ).then((m) => m.SocialServiceStatisticsOverviewTargetGroupsComponent),
            },
            {
                path: SocialStatisticsTypeEnum.OVERVIEW_SERVICE_PERMANENT_RESIDENCE,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsOverviewServicePermanentResidence,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-overview-permanent-residence/social-service-statistics-overview-permanent-residence.component'
                        ).then((m) => m.SocialServiceStatisticsOverviewPermanentResidenceComponent),
            },
            {
                path: SocialStatisticsTypeEnum.OVERVIEW_SERVICE_PROVISION_MUNICIPALITIES,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsOverviewServiceProvisionMunicipalities,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-overview-provision-municipalities/social-service-statistics-overview-provision-municipalities.component'
                        ).then((m) => m.SocialServiceStatisticsOverviewProvisionMunicipalitiesComponent),
            },
            {
                path: SocialStatisticsTypeEnum.OVERVIEW_SERVICE_CLIENT_WORKPLACES,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsOverviewServiceClientWorkplaces,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-overview-client-workplaces/social-service-statistics-overview-client-workplaces.component'
                        ).then((m) => m.SocialServiceStatisticsOverviewClientWorkplacesComponent),
            },
            {
                path: SocialStatisticsTypeEnum.OVERVIEW_SERVICE_FOLDER_STATUSES,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsOverviewServiceFolderStatuses,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-overview-folder-statuses/social-service-statistics-overview-folder-statuses.component'
                        ).then((m) => m.SocialServiceStatisticsOverviewFolderStatusesComponent),
            },
            {
                path: SocialStatisticsTypeEnum.OVERVIEW_SERVICE_BED_CAPACITY,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsOverviewServiceBedCapacity,
                        alias: BreadcrumbsEnum.Statistics,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-overview-bed-capacity/social-service-statistics-overview-bed-capacity.component'
                        ).then((m) => m.SocialServiceStatisticsOverviewBedCapacityComponent),
            },
            {
                path: SocialStatisticsTypeEnum.OVERVIEW_SERVICE_EMPLOYMENTS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialServiceStatisticsOverviewServiceEmployments,
                        alias: BreadcrumbsEnum.SocialServiceStatisticsOverviewServiceEmployments,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-overview-employments/social-service-statistics-overview-employments.component'
                        ).then(
                        (m) => m.SocialServiceStatisticsOverviewEmploymentsComponent,
                    ),
            },
            {
                path: SocialStatisticsTypeEnum.OVERVIEW_SERVICE_WORKLOAD_USAGE,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsOverviewServiceWorkloadUsage,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsOverviewServiceWorkloadUsage,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-overview-workload-usage/social-service-statistics-overview-workload-usage.component'
                        ).then((m) => m.SocialServiceStatisticsOverviewWorkloadUsageComponent),
            },
            {
                path: SocialStatisticsTypeEnum.FINANCIAL_DEPOSITS_CLIENTS_SUMMARY,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialFinancialDeposit,
                        alias: BreadcrumbsEnum.SocialFinancialDeposit,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-financial-deposits-clients-summary/social-service-statistics-financial-deposits-clients-summary.component'
                        ).then((m) => m.SocialServiceStatisticsFinancialDepositsClientsSummaryComponent),
            },
            {
                path: SocialStatisticsTypeEnum.BILLING_SERVICE_MUNICIPALITY,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialBillingDocuments,
                        alias: BreadcrumbsEnum.SocialBillingDocuments,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-billing-service-municipality/social-service-statistics-billing-service-municipality.component'
                        ).then((m) => m.SocialServiceStatisticsBillingServiceMunicipalityComponent),
            },
            {
                path: SocialStatisticsTypeEnum.BILLING_SERVICE_ORP,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialBillingDocuments,
                        alias: BreadcrumbsEnum.SocialBillingDocuments,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-billing-service-orp/social-service-statistics-billing-service-orp.component'
                        ).then((m) => m.SocialServiceStatisticsBillingServiceOrpComponent),
            },
            {
                path: SocialStatisticsTypeEnum.BILLING_SERVICE_WORKPLACE,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialBillingDocuments,
                        alias: BreadcrumbsEnum.SocialBillingDocuments,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-billing-service-workplace/social-service-statistics-billing-service-workplace.component'
                        ).then((m) => m.SocialServiceStatisticsBillingServiceWorkplaceComponent),
            },
            {
                path: SocialStatisticsTypeEnum.BILLING_ITEMS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialBillingDocuments,
                        alias: BreadcrumbsEnum.SocialBillingDocuments,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-billing-items/social-service-statistics-billing-items.component'
                        ).then((m) => m.SocialServiceStatisticsBillingItemsComponent),
            },
            {
                path: SocialStatisticsTypeEnum.BILLING_ITEMS_OFFERED_ACTIVITIES,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialBillingDocuments,
                        alias: BreadcrumbsEnum.SocialBillingDocuments,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-billing-items-offered-activities/social-service-statistics-billing-items-offered-activities.component'
                        ).then((m) => m.SocialServiceStatisticsBillingItemsOfferedActivitiesComponent),
            },
            {
                path: SocialStatisticsTypeEnum.BILLING_ITEMS_OFFERED_ACTIVITY_GROUPS,
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.SocialBillingDocuments,
                        alias: BreadcrumbsEnum.SocialBillingDocuments,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/social-service/statistics/social-service-statistics-billing-items-offered-activity-groups/social-service-statistics-billing-items-offered-activity-groups.component'
                        ).then((m) => m.SocialServiceStatisticsBillingItemsOfferedActivityGroupsComponent),
            },
        ],
    },
];

const healthCareRoutes: Routes = [
    {
        // Overview page
        path: 'works',
        title: xCarolName,
        data: {
            trashEntityName: TrashEntityTypeEnum.WORK,
            visit: { skip: true },
            breadcrumb: BreadcrumbsEnum.Works,
        },
        loadComponent: () =>
            import('./pages/services/works-page/works-page.component').then(
                (m) => m.WorksPageComponent,
            ),
        children: [
            {
                // Create pages
                path: 'visit',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.WorkVisit },
                canDeactivate: [unsavedChangesGuard],
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-visit-page/work-visit-page.component'
                        ).then((m) => m.WorkVisitPageComponent),
                children: [
                    {
                        path: ':workId',
                        canDeactivate: [unsavedChangesGuard],
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/medical-service/work/pages/work-visit-page/work-visit-page.component'
                                ).then((m) => m.WorkVisitPageComponent),
                    },
                ],
            },
            {
                path: 'unrealized-visit',
                title: xCarolName,
                canDeactivate: [unsavedChangesGuard],
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkUnrealizedVisit,
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-unrealized-visit-page/work-unrealized-visit-page.component'
                        ).then((m) => m.WorkUnrealizedVisitPageComponent),
                children: [
                    {
                        path: ':workId',
                        canDeactivate: [unsavedChangesGuard],
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/medical-service/work/pages/work-unrealized-visit-page/work-unrealized-visit-page.component'
                                ).then((m) => m.WorkUnrealizedVisitPageComponent),
                    },
                ],
            },
            {
                path: 'service-record',
                title: xCarolName,
                canDeactivate: [unsavedChangesGuard],
                data: { breadcrumb: BreadcrumbsEnum.WorkServiceRecord },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-service-record-page/work-service-record-page.component'
                        ).then((m) => m.WorkServiceRecordPageComponent),
                children: [
                    {
                        path: ':workId',
                        canDeactivate: [unsavedChangesGuard],
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/medical-service/work/pages/work-service-record-page/work-service-record-page.component'
                                ).then((m) => m.WorkServiceRecordPageComponent),
                    },
                ],
            },
            {
                path: 'extramural-care',
                title: xCarolName,
                canDeactivate: [unsavedChangesGuard],
                data: { breadcrumb: BreadcrumbsEnum.ExtramuralCare },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-extramural-care-page/work-extramural-care-page.component'
                        ).then((m) => m.WorkExtramuralCarePageComponent),
                children: [
                    {
                        path: ':workId',
                        canDeactivate: [unsavedChangesGuard],
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/medical-service/work/pages/work-extramural-care-page/work-extramural-care-page.component'
                                ).then((m) => m.WorkExtramuralCarePageComponent),
                    },
                ],
            },
            {
                path: 'residuary-care',
                title: xCarolName,
                canDeactivate: [unsavedChangesGuard],
                data: { breadcrumb: BreadcrumbsEnum.WorkResiduaryCare },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-residuary-care-page/work-residuary-care-page.component'
                        ).then((m) => m.WorkResiduaryCarePageComponent),
                children: [
                    {
                        path: ':workId',
                        canDeactivate: [unsavedChangesGuard],
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/medical-service/work/pages/work-residuary-care-page/work-residuary-care-page.component'
                                ).then((m) => m.WorkResiduaryCarePageComponent),
                    },
                ],
            },
            {
                path: 'intervention',
                title: xCarolName,
                canDeactivate: [unsavedChangesGuard],
                data: { breadcrumb: BreadcrumbsEnum.WorkIntervention },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-intervention-page/work-intervention-page.component'
                        ).then((m) => m.WorkInterventionPageComponent),
                children: [
                    {
                        path: ':workId',
                        canDeactivate: [unsavedChangesGuard],
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/medical-service/work/pages/work-intervention-page/work-intervention-page.component'
                                ).then((m) => m.WorkInterventionPageComponent),
                    },
                ],
            },
            {
                // Create specialist record
                path: 'specialist-record',
                title: xCarolName,
                canDeactivate: [unsavedChangesGuard],
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkSpecialistRecord,
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-specialist-record-page/work-specialist-record-page.component'
                        ).then((m) => m.WorkSpecialistRecordPageComponent),
                children: [
                    {
                        path: ':workId',
                        canDeactivate: [unsavedChangesGuard],
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/medical-service/work/pages/work-specialist-record-page/work-specialist-record-page.component'
                                ).then((m) => m.WorkSpecialistRecordPageComponent),
                    },
                ],
            },
            {
                // Create doctor record
                path: 'doctor-record',
                title: xCarolName,
                canDeactivate: [unsavedChangesGuard],
                data: { breadcrumb: BreadcrumbsEnum.WorkDoctorRecord },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-doctor-record-page/work-doctor-record-page.component'
                        ).then((m) => m.WorkDoctorRecordPageComponent),
                children: [
                    {
                        path: ':workId',
                        canDeactivate: [unsavedChangesGuard],
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/medical-service/work/pages/work-doctor-record-page/work-doctor-record-page.component'
                                ).then((m) => m.WorkDoctorRecordPageComponent),
                    },
                ],
            },
            {
                path: 'operational-report',
                title: xCarolName,
                canDeactivate: [unsavedChangesGuard],
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkOperationalReport,
                },
                loadComponent: () =>
                    import(
                        '../../components/common-service/work/pages/work-operational-report-page/work-operational-report-page.component'
                        ).then((m) => m.WorkOperationalReportPageComponent),
                children: [
                    {
                        path: ':workId',
                        title: xCarolName,
                        canDeactivate: [unsavedChangesGuard],
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.Id,
                                alias: BreadcrumbsEnum.Id,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/common-service/work/pages/work-operational-report-page/work-operational-report-page.component'
                                ).then((m) => m.WorkOperationalReportPageComponent),
                    },
                ],
            },
        ],
    },
    {
        path: 'scheduler',
        title: xCarolName,
        data: {
            trashEntityName: TrashEntityTypeEnum.CALENDAR_ITEM,
            breadcrumb: {
                label: BreadcrumbsEnum.Scheduler,
                alias: BreadcrumbsEnum.Scheduler,
            },
        },
        loadComponent: () => featureOn.HEALTHCARE_CALENDAR_MIGRATION
            ? import('../../components/medical-service/calendar/health-care-calendar.component').then(m => m.HealthCareCalendarComponent)
            : import('../../components/medical-service/scheduler/scheduler-page/scheduler-page.component').then(m => m.SchedulerPageComponent),
    },
    {
        path: 'realized-work',
        title: xCarolName,
        data: {
            trashEntityName: TrashEntityTypeEnum.WORK,
            breadcrumb: BreadcrumbsEnum.RealizedWorks,
        },
        children: [
            {
                path: '',
                pathMatch: 'full',
                title: xCarolName,
                canActivate: [HealthCareRealizedWorkRedirectGuardGuard],
                component: EmptyPageComponent,
            },
            // List pages
            {
                path: 'list/visit',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.WorkVisits },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-visits-page/work-visits-page.component'
                        ).then((m) => m.WorkVisitsPageComponent),
            },
            {
                path: 'list/performed-procedures',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.PerformedProcedures,
                },
                loadComponent: () =>
                    import(
                        '../content/pages/services/realized-work-pages/performed-procedures-page/performed-procedures-page.component'
                        ).then((m) => m.PerformedProceduresPageComponent),
            },
            {
                path: 'list/unrealized-visit',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkUnrealizedVisits,
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-unrealized-visits-page/work-unrealized-visits-page.component'
                        ).then((m) => m.WorkUnrealizedVisitsPageComponent),
            },
            {
                path: 'list/daily-record',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkDailyRecords,
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-daily-records-page/work-daily-records-page.component'
                        ).then((m) => m.WorkDailyRecordsPageComponent),
            },
            {
                path: 'list/service-record',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkServiceRecords,
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-service-records-page/work-service-records-page.component'
                        ).then((m) => m.WorkServiceRecordsPageComponent),
            },
            {
                path: 'list/extramural-care',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.ExtramuralCare },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-extramural-cares-page/work-extramural-cares-page.component'
                        ).then((m) => m.WorkExtramuralCaresPageComponent),
            },
            {
                path: 'list/residuary-care',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkResiduaryCares,
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-residuary-cares-page/work-residuary-cares-page.component'
                        ).then((m) => m.WorkResiduaryCaresPageComponent),
            },
            {
                path: 'list/intervention',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkInterventions,
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-interventions-page/work-interventions-page.component'
                        ).then((m) => m.WorkInterventionsPageComponent),
            },
            {
                path: 'list/specialist-record',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkSpecialistRecords,
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-specialist-records-page/work-specialist-records-page.component'
                        ).then((m) => m.WorkSpecialistRecordsPageComponent),
            },
            {
                path: 'list/operational-reports',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkOperationalReports,
                },
                loadComponent: () =>
                    import(
                        '../../components/common-service/work/pages/work-operational-reports-page/work-operational-reports-page.component'
                        ).then((m) => m.WorkOperationalReportsPageComponent),
            },
            {
                path: 'list/doctor-record',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.WorkDoctorRecords,
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/work/pages/work-doctor-records-page/work-doctor-records-page.component'
                        ).then((m) => m.WorkDoctorRecordsPageComponent),
            },
        ],
    },
    {
        path: 'billing',
        title: xCarolName,
        data: {
            breadcrumb: {
                label: BreadcrumbsEnum.Billing,
                disable: true,
            },
        },
        children: [
            {
                path: '',
                pathMatch: 'full',
                title: xCarolName,
                redirectTo: 'create',
            },
            {
                path: 'create',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.BillingNewCreate },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/billing/billing-create/billing-create-page/billing-create-page.component'
                        ).then((m) => m.BillingCreatePageComponent),
            },
            {
                path: 'create',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.BillingNewCreate },
                children: [
                    {
                        path: 'confirm/:billingId',
                        title: xCarolName,
                        data: {
                            breadcrumb: BreadcrumbsEnum.BillingNewConfirm,
                        },
                        loadComponent: () =>
                            import(
                                '../../components/medical-service/billing/billing-create/billing-confirm-page/billing-confirm-page.component'
                                ).then((m) => m.BillingConfirmPageComponent),
                    },
                    {
                        path: 'confirm/:billingId',
                        title: xCarolName,
                        data: {
                            visit: { group: true },
                            breadcrumb: BreadcrumbsEnum.BillingNewConfirm,
                        },
                        children: billingDetailRoutes,
                    },
                ],
            },
            {
                path: 'refused',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.BillingRefused },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/billing/billing-refused/billing-refused-page/billing-refused-page.component'
                        ).then((m) => m.BillingRefusedPageComponent),
            },
            {
                path: 'unbilled',
                title: xCarolName,
                data: {
                    trashEntityName: TrashEntityTypeEnum.WORK,
                    breadcrumb: BreadcrumbsEnum.BillingUnbilledWork,
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/billing/billing-unbilled-work/billing-unbilled-work-page/billing-unbilled-work-page.component'
                        ).then((m) => m.BillingUnbilledWorkPageComponent),
            },
            {
                path: 'output',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.BillingAccountingOutput,
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/billing/billing-accounting-output/billing-accounting-output-page/billing-accounting-output-page.component'
                        ).then((m) => m.BillingAccountingOutputPageComponent),
            },
            {
                path: 'created',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.BillingCreatedBillings,
                        disable: true,
                    },
                },
                children: [
                    {
                        path: ':insuranceAbbr',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.BILLING,
                            breadcrumb: {
                                label: BreadcrumbsEnum.BillingCreatedBillingsDetail,
                                alias: BreadcrumbsEnum.BillingCreatedBillingsDetail,
                            },
                        },
                        loadComponent: () =>
                            import(
                                '../../components/medical-service/billing/billing-created-billings/billing-created-billings-page/billing-created-billings-page.component'
                                ).then(
                                (m) => m.BillingCreatedBillingsPageComponent,
                            ),
                    },
                    {
                        path: ':insuranceAbbr',
                        title: xCarolName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.BillingCreatedBillingsDetail,
                                alias: BreadcrumbsEnum.BillingCreatedBillingsDetail,
                            },
                        },
                        children: [
                            {
                                path: 'unprocessed',
                                title: xCarolName,
                                data: {
                                    breadcrumb:
                                    BreadcrumbsEnum.BillingUnprocessedBillings,
                                },
                                loadComponent: () =>
                                    import(
                                        '../../components/medical-service/billing/billing-unprocessed-billings/billing-unprocessed-billings-page/billing-unprocessed-billings-page.component'
                                        ).then(
                                        (m) =>
                                            m.BillingUnprocessedBillingsPageComponent,
                                    ),
                            },
                            {
                                path: 'unprocessed',
                                title: xCarolName,
                                data: {
                                    breadcrumb:
                                    BreadcrumbsEnum.BillingUnprocessedBillings,
                                },
                                children: billingDetailRoutes,
                            },
                            ...billingDetailRoutes,
                        ],
                    },
                ],
            },
        ],
    },
    {
        path: 'tools',
        title: xCarolName,
        data: { breadcrumb: BreadcrumbsEnum.Tools, disable: true },
        children: [
            {
                path: 'batch-print',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.ToolsBatchPrint },
                component: MedicalServiceBatchPrintPageComponent,
            },
            {
                path: 'missing-procedures',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.ToolsMissingProcedures },
                component: ToolsMissingProceduresPageComponent,
            },
            {
                path: 'missing-doctor-records',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.ToolsMissingDoctorRecords },
                component: ToolsMissingDoctorRecordsPageComponent,
            },
            {
                path: 'medication-preparation',
                title: xCarolName,
                data: {
                    breadcrumb: BreadcrumbsEnum.ToolsMedicationPreparation,
                },
                loadComponent: () => featureOn.HEALTHCARE_CALENDAR_MIGRATION
                    ? import('./pages/services/tools-medication-preparation-page/health-care-medication-preparation-page.component')
                        .then(m => m.HealthCareMedicationPreparationPageComponent)
                    : import('./pages/services/tools-medication-preparation-page/tools-medication-preparation-page.component')
                        .then(m => m.ToolsMedicationPreparationPageComponent),
            },
        ],
    },
    {
        path: 'statistics',
        title: generalName,
        data: {
            breadcrumb: {
                label: BreadcrumbsEnum.Statistics,
                alias: BreadcrumbsEnum.Statistics,
            },
        },
        children: [
            {
                path: 'workers',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsWorkers,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsWorkers,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-workers/medical-service-statistics-workers.component'
                        ).then((m) => m.MedicalServiceStatisticsWorkersComponent),
            },
            {
                path: 'operationalReport',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsOperationalReport,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsOperationalReport,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-operational-report/medical-service-statistics-operational-report.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsOperationalReportComponent,
                    ),
            },
            {
                path: 'folders',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsFolders,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsFolders,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-folders/medical-service-statistics-folders.component'
                        ).then((m) => m.MedicalServiceStatisticsFoldersComponent),
            },
            {
                path: 'deceasedClientsContacts',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsDeceasedClientsContacts,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsDeceasedClientsContacts,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-deceased-clients-contacts/medical-service-statistics-deceased-clients-contacts.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsDeceasedClientsContactsComponent,
                    ),
            },
            {
                path: 'clientsCountInsurance',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsClientsCountInsurance,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsClientsCountInsurance,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-client-count-insurance/medical-service-statistics-client-count-insurance.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsClientCountInsuranceComponent,
                    ),
            },
            {
                path: 'clientsCountDoctors',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsClientsCountDoctors,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsClientsCountDoctors,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-client-count-doctors/medical-service-statistics-client-count-doctors.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsClientCountDoctorsComponent,
                    ),
            },
            {
                path: 'clientsCountDiagnoses',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsClientsCountDiagnoses,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsClientsCountDiagnoses,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-client-count-diagnoses/medical-service-statistics-client-count-diagnoses.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsClientCountDiagnosesComponent,
                    ),
            },
            {
                path: 'clientsCountProcedure',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsClientsCountProcedure,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsClientsCountProcedure,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-client-count-procedure/medical-service-statistics-client-count-procedure.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsClientCountProcedureComponent,
                    ),
            },
            {
                path: 'clientsCountBonificationInsurance',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsClientsCountBonificationInsurance,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsClientsCountBonificationInsurance,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-client-count-bonification-insurance/medical-service-statistics-client-count-bonification-insurance.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsClientCountBonificationInsuranceComponent,
                    ),
            },
            {
                path: 'clientsProcedure',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsClientsProcedure,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsClientsProcedure,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-clients-procedure/medical-service-statistics-clients-procedure.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsClientsProcedureComponent,
                    ),
            },
            {
                path: 'proceduresClient',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsProceduresClient,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsProceduresClient,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-by-client/medical-service-statistics-by-client.component'
                        ).then((m) => m.MedicalServiceStatisticsByClientComponent),
            },
            {
                path: 'proceduresClientDay',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsProceduresClientDay,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsProceduresClientDay,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-by-client-day/medical-service-statistics-by-client-day.component'
                        ).then(
                        (m) => m.MedicalServiceStatisticsByClientDayComponent,
                    ),
            },
            {
                path: 'proceduresClientInsurance',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsProceduresClientInsurance,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsProceduresClientInsurance,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-by-client-insurance/medical-service-statistics-by-client-insurance.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsByClientInsuranceComponent,
                    ),
            },
            {
                path: 'proceduresClientVoucher',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsProceduresClientVoucher,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsProceduresClientVoucher,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-by-client-voucher/medical-service-statistics-by-client-voucher.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsByClientVoucherComponent,
                    ),
            },
            {
                path: 'proceduresClientPalliative',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsProceduresClientPalliative,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsProceduresClientPalliative,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-by-client-palliative/medical-service-statistics-by-client-palliative.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsByClientPalliativeComponent,
                    ),
            },
            {
                path: 'proceduresClientWorkersDay',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsProceduresClientWorkersDay,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsProceduresClientWorkersDay,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-by-client-workers-day/medical-service-statistics-by-client-workers-day.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsByClientWorkersDayComponent,
                    ),
            },
            {
                path: 'proceduresBearers',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsProceduresBearers,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsProceduresBearers,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-procedure-bearers/medical-service-statistics-procedure-bearers.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsProcedureBearersComponent,
                    ),
            },
            {
                path: 'proceduresCount',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsProceduresCount,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsProceduresCount,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-procedure-count/medical-service-statistics-procedure-count.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsProcedureCountComponent,
                    ),
            },
            {
                path: 'performedVisitsClient',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsPerformedVisitsClient,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsPerformedVisitsClient,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-performed-visits-client/medical-service-statistics-performed-visits-client.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsPerformedVisitsClientComponent,
                    ),
            },
            {
                path: 'performedVisitsWorkers',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsPerformedVisitsWorkers,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsPerformedVisitsWorkers,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-performed-visits-workers/medical-service-statistics-performed-visits-workers.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsPerformedVisitsWorkersComponent,
                    ),
            },
            {
                path: 'visitsWorker',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsVisitsWorker,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsVisitsWorker,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-visit-workers/medical-service-statistics-visit-workers.component'
                        ).then(
                        (m) => m.MedicalServiceStatisticsVisitWorkersComponent,
                    ),
            },
            {
                path: 'visitsInsurance',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsVisitsInsurance,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsVisitsInsurance,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-visit-insurance/medical-service-statistics-visit-insurance.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsVisitInsuranceComponent,
                    ),
            },
            {
                path: 'visitsWorkplace',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsVisitsWorkplace,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsVisitsWorkplace,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-visit-workplace/medical-service-statistics-visit-workplace.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsVisitWorkplaceComponent,
                    ),
            },
            {
                path: 'visitsCity',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsVisitsCity,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsVisitsCity,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-visit-city/medical-service-statistics-visit-city.component'
                        ).then((m) => m.MedicalServiceStatisticsVisitCityComponent),
            },
            {
                path: 'visitsConsultationDoctorRecord',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsVisitsConsultationDoctorRecord,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsVisitsConsultationDoctorRecord,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-visit-consultation-doctor-record/medical-service-statistics-visit-consultation-doctor-record.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsVisitConsultationDoctorRecordComponent,
                    ),
            },
            {
                path: 'visitsConsultationSpecialistRecord',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsVisitsConsultationSpecialistRecord,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsVisitsConsultationSpecialistRecord,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-visit-consultation-specialist-record/medical-service-statistics-visit-consultation-specialist-record.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsVisitConsultationSpecialistRecordComponent,
                    ),
            },
            {
                path: 'visitsConsultationNurseRecord',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsVisitsConsultationNurseRecord,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsVisitsConsultationNurseRecord,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-visit-consultation-nurse-record/medical-service-statistics-visit-consultation-nurse-record.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsVisitConsultationNurseRecordComponent,
                    ),
            },
            {
                path: 'dailyRecordsClientTerm',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsDailyRecordsClientTerm,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsDailyRecordsClientTerm,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-daily-record-client/medical-service-statistics-daily-record-client.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsDailyRecordClientComponent,
                    ),
            },
            {
                path: 'dailyRecordsClientVoucher',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsDailyRecordsClientVoucher,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsDailyRecordsClientVoucher,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-daily-record-client-voucher/medical-service-statistics-daily-record-client-voucher.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsDailyRecordClientVoucherComponent,
                    ),
            },
            {
                path: 'dailyRecordsClientVoucher',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsDailyRecordsClientVoucher,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsDailyRecordsClientVoucher,
                    },
                },
                component: EmptyPageComponent,
            },
            {
                path: 'clientsToArchive',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsClientsToArchive,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsClientsToArchive,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-clients-record-archive/medical-service-statistics-clients-record-archive.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsClientsRecordArchiveComponent,
                    ),
            },
            {
                path: 'doctorRecordsToArchive',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsDoctorRecordsToArchive,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsDoctorRecordsToArchive,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-doctors-record-archive/medical-service-statistics-doctors-record-archive.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsDoctorsRecordArchiveComponent,
                    ),
            },
            {
                path: 'clientsSignalCode',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsClientsSignalCode,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsClientsSignalCode,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-client-signal-code/medical-service-statistics-client-signal-code.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsClientSignalCodeComponent,
                    ),
            },
            {
                path: 'puro',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsPuro,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsPuro,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-puro-pmup/medical-service-statistics-puro-pmup.component'
                        ).then((m) => m.MedicalServiceStatisticsPuroPmupComponent),
            },
            {
                path: 'yearlyHealthcareProvider',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsYearlyHealthcareProvider,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsYearlyHealthcareProvider,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-yearly-healthcare-provider/medical-service-statistics-yearly-healthcare-provider.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsYearlyHealthcareProviderComponent,
                    ),
            },
            {
                path: 'acho',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsAcho,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsAcho,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-acho/medical-service-statistics-acho.component'
                        ).then((m) => m.MedicalServiceStatisticsAchoComponent),
            },
            {
                path: 'overviewServiceFolderPersons',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsOverviewServiceFolderPersons,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsOverviewServiceFolderPersons,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-folders-person/medical-service-statistics-folders-person.component'
                        ).then(
                        (m) => m.MedicalServiceStatisticsFoldersPersonComponent,
                    ),
            },
            {
                path: 'overviewServicePermanentResidence',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsOverviewServicePermanentResidence,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsOverviewServicePermanentResidence,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-permanent-residence/medical-service-statistics-permanent-residence.component'
                        ).then(
                        (m) =>
                            m.MedicalServiceStatisticsPermanentResidenceComponent,
                    ),
            },
            {
                path: 'overviewServiceEmployments',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsOverviewServiceEmployments,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsOverviewServiceEmployments,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-employments/medical-service-statistics-employments.component'
                        ).then(
                        (m) => m.MedicalServiceStatisticsEmploymentsComponent,
                    ),
            },
            {
                path: 'overviewServiceWorkloadUsage',
                title: xCarolName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.MedicalServiceStatisticsOverviewServiceWorkloadUsage,
                        alias: BreadcrumbsEnum.MedicalServiceStatisticsOverviewServiceWorkloadUsage,
                    },
                },
                loadComponent: () =>
                    import(
                        '../../components/medical-service/statistics/medical-service-statistics-workload-usage/medical-service-statistics-workload-usage.component'
                        ).then(
                        (m) => m.MedicalServiceStatisticsWorkloadUsageComponent,
                    ),
            },
        ],
    },
];

export const contentRoutes: Routes = [
    {
        path: '',
        providers: [
            provideState(personFeatureKey, personReducer),
            provideState(serviceFeatureKey, serviceReducer),
            provideState(userFeatureKey, userReducer),
            provideState(sharedFeatureKey, sharedReducer),
            provideState(organizationFeatureKey, organizationReducer),
            provideEffects(PersonEffects, ServiceEffects, UserEffects),
            provideChildTranslateService({ extend: true }),
            UserService,
            PersonService,
            ServiceService,
            SporitelnaService,
            {
                provide: Window,
                useValue: window,
            },
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => SelectColorComponent),
                multi: true,
            },
            apolloLinkProvider,
            SelectColorComponent,
        ],
        component: ContentComponent,
        data: { reloadOn: ['organizationChange'] },
        children: [
            {
                path: 'organizations',
                title: generalName,
                data: {
                    breadcrumb: {
                        label: BreadcrumbsEnum.Organizations,
                        alias: BreadcrumbsEnum.Organizations,
                    },
                },
                component: OrganizationsPageComponent,
            },
            {
                path: 'organizations',
                title: generalName,
                data: {
                    visit: { skip: true },
                    breadcrumb: {
                        label: BreadcrumbsEnum.Organizations,
                        alias: BreadcrumbsEnum.Organizations,
                    },
                },
                children: [
                    {
                        path: 'create',
                        title: generalName,
                        data: {
                            visit: { skip: true },
                            breadcrumb: {
                                label: BreadcrumbsEnum.CreateOrganization,
                                alias: BreadcrumbsEnum.CreateOrganization,
                            },
                        },
                        component: CreateOrganizationPageComponent,
                    },
                    {
                        path: ':organizationId',
                        title: generalName,
                        data: {
                            visit: { group: true },
                            trashEntityName: [
                                TrashEntityTypeEnum.PLATFORM_BILLING_CONTRACT,
                                TrashEntityTypeEnum.PLATFORM_BILLING_BASIS,
                            ],
                            breadcrumb: {
                                label: BreadcrumbsEnum.Organization,
                                alias: BreadcrumbsEnum.Organization,
                            },
                        },
                        component: OrganizationDetailPageComponent,
                    },
                    {
                        path: ':organizationId',
                        title: generalName,
                        data: {
                            visit: { group: true },
                            breadcrumb: {
                                label: BreadcrumbsEnum.Organization,
                                alias: BreadcrumbsEnum.Organization,
                            },
                        },
                        children: [
                            {
                                path: 'acl',
                                data: {
                                    breadcrumb: {
                                        label: BreadcrumbsEnum.Groups,
                                        alias: BreadcrumbsEnum.Groups,
                                    },
                                },
                                component: AclGroupsPageComponent,
                            },
                            {
                                path: 'centers/:centerId',
                                data: {
                                    breadcrumb: {
                                        label: BreadcrumbsEnum.Center,
                                        alias: BreadcrumbsEnum.Center,
                                    },
                                },
                                component: CenterDetailPageComponent,
                            },
                        ],
                    },
                ],
            },
            {
                path: 'dashboard',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.ServiceDashboard },
                component: DashboardPageComponent,
            },
            {
                path: 'trash',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.Trash },
                canMatch: [() => Boolean(featureOn.TRASH)],
                loadComponent: () => import('./pages/trash/trash-page.component').then((m) => m.TrashPageComponent),
            },
            {
                path: 'deska-monitor',
                data: { breadcrumb: BreadcrumbsEnum.DeskaMonitor },
                loadChildren: () =>
                    import('./pages/deska-monitor/deska-monitor.routes').then(
                        (m) => m.DESKA_MONITOR_ROUTES,
                    ),
            },
            {
                path: 'editorial-poc',
                title: 'Redakční systém',
                data: {
                    visit: { skip: true },
                    breadcrumb: { skip: true },
                },
                loadComponent: () =>
                    import('./pages/editorial-poc/editorial-poc.component').then(
                        (m) => m.EditorialPocComponent,
                    ),
            },
            {
                path: 'my-calendar',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.ServiceDashboard },
                loadComponent: () =>
                    import('../../components/my-calendar/my-calendar.component').then(
                        (m) => m.MyCalendarComponent,
                    ),
            },
            {
                path: 'social',
                redirectTo: '/content/dashboard',
                pathMatch: 'full',
            },
            {
                path: 'social',
                title: xCarolName,
                data: {
                    visit: { skip: true },
                    breadcrumb: BreadcrumbsEnum.SocialServices,
                    serviceTypeContext: ServiceTypeEnum.SOCIAL,
                    breadcrumbMenuContext: BreadcrumbsMenuContextEnum.SOCIAL_SERVICE,
                },
                children: [...baseServiceTypeRoutes, ...socialRoutes],
            },
            {
                path: 'health-care',
                redirectTo: '/content/dashboard',
                pathMatch: 'full',
            },
            {
                path: 'health-care',
                title: xCarolName,
                data: {
                    visit: { skip: true },
                    breadcrumb: BreadcrumbsEnum.HealthCareServices,
                    serviceTypeContext: ServiceTypeEnum.HEALTH_CARE,
                    breadcrumbMenuContext: BreadcrumbsMenuContextEnum.HEALTH_CARE,
                },
                children: [...baseServiceTypeRoutes, ...healthCareRoutes],
            },
            {
                path: 'services',
                title: xCarolName,
                data: {
                    trashEntityName: TrashEntityTypeEnum.SERVICE,
                    breadcrumb: BreadcrumbsEnum.Services,
                },
                component: ServicesPageComponent,
            },
            {
                path: 'services',
                title: xCarolName,
                data: {
                    visit: { skip: true },
                    breadcrumb: BreadcrumbsEnum.Services,
                },
                children: [
                    {
                        path: ':serviceId',
                        title: xCarolName,
                        data: {
                            visit: { group: true, skip: true },
                            breadcrumb: {
                                label: BreadcrumbsEnum.Service,
                                alias: BreadcrumbsEnum.Service,
                            },
                        },
                        canActivate: [ServiceIdGuard],
                        component: ServiceDetailPageComponent,
                        children: [
                            // {
                            //     path: '',
                            //     pathMatch: 'full',
                            //     title: xCarolName,
                            //     redirectTo: 'basic-information',
                            // },
                            {
                                path: 'basic-information',
                                title: xCarolName,
                                data: {
                                    breadcrumb:
                                    BreadcrumbsEnum.ServiceBasicInfo,
                                },
                                component:
                                ServicesBasicInformationPageComponent,
                            },
                            {
                                path: 'archive-disposal',
                                title: xCarolName,
                                canMatch: [() => Boolean(featureOn.ARCHIVE_DISPOSAL)],
                                data: { breadcrumb: BreadcrumbsEnum.ServiceArchiveDisposal },
                                loadComponent: () =>
                                    import(
                                        './pages/services/service-archive-disposal-page/service-archive-disposal-page.component'
                                        ).then((m) => m.ServiceArchiveDisposalPageComponent),
                            },
                            {
                                path: 'settings',
                                title: xCarolName,
                                data: {
                                    breadcrumb: BreadcrumbsEnum.ServiceSettings,
                                },
                                component: ServicesSettingsPageComponent,
                                children: [
                                    {
                                        path: '',
                                        pathMatch: 'full',
                                        title: xCarolName,
                                        redirectTo: 'general',
                                    },
                                    {
                                        path: 'general',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsGeneralInfo,
                                        },
                                        component:
                                        ServicesSettingsGeneralInfoPageComponent,
                                    },
                                    {
                                        path: 'dashboard',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsDashboard,
                                        },
                                        component:
                                        ServiceSettingDashboardPageComponent,
                                    },
                                    {
                                        path: 'calendar',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsCalendar,
                                        },
                                        loadComponent: () =>
                                            import(
                                                './pages/services/settings/service-settings-calendar/service-settings-calendar.component'
                                                ).then(
                                                (m) =>
                                                    m.ServiceSettingsCalendarComponent,
                                            ),
                                    },
                                    {
                                        path: 'statistics',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsStatistics,
                                        },
                                        loadComponent: () =>
                                            import(
                                                '../../components/common-service/statistics/service-statistics-settings/service-statistics-settings.component'
                                                ).then((m) => m.ServiceStatisticsSettingsComponent),
                                    },
                                    {
                                        path: 'contacts',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsContacts,
                                        },
                                        component:
                                        ServiceSettingsContactsComponent,
                                    },
                                    {
                                        path: 'forms',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsForms,
                                        },
                                        component:
                                        ServiceSettingsFormsComponent,
                                    },
                                    {
                                        path: 'voucher',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsVoucher,
                                        },
                                        component:
                                        ServiceSettingsVoucherComponent,
                                    },
                                    {
                                        path: 'anamnesis',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsAnamnesis,
                                        },
                                        component:
                                        ServiceSettingsAnamnesisComponent,
                                    },
                                    {
                                        path: 'medication',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsMedication,
                                        },
                                        component:
                                        ServiceSettingsMedicationComponent,
                                    },
                                    {
                                        path: 'care-plan',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsCarePlan,
                                        },
                                        component:
                                        ServiceSettingsCarePlanComponent,
                                    },
                                    {
                                        path: 'epicrisis',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsEpicrisis,
                                        },
                                        component:
                                        ServiceSettingsEpicrisisComponent,
                                    },
                                    {
                                        path: 'eprescriptions',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsEPrescriptions,
                                        },
                                        component:
                                        ServiceSettingsEPrescriptionsComponent,
                                    },
                                    {
                                        path: 'sick-notes',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsSickNotes,
                                        },
                                        component:
                                        ServiceSettingsSickNotesComponent,
                                    },
                                    {
                                        path: 'visit-type',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsVisitType,
                                        },
                                        component:
                                        ServiceSettingsVisitTypeComponent,
                                    },
                                    {
                                        path: 'nurse-record',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsNurserRecord,
                                        },
                                        component:
                                        ServiceSettingsNurseRecordsComponent,
                                    },
                                    {
                                        path: 'individual-plan',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.IndividualPlans,
                                        },
                                        loadComponent: () =>
                                            import(
                                                '../../components/social-service/setting/service-settings-individual-plan/service-settings-individual-plan.component'
                                                ).then(
                                                (m) =>
                                                    m.ServiceSettingsIndividualPlanComponent,
                                            ),
                                    },
                                    {
                                        path: 'contracts',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.Contracts,
                                        },
                                        loadComponent: () =>
                                            import(
                                                '../../components/social-service/setting/service-settings-contracts/service-settings-contracts.component'
                                                ).then(
                                                (m) =>
                                                    m.ServiceSettingsContractsComponent,
                                            ),
                                    },
                                    {
                                        path: 'attendance',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: BreadcrumbsEnum.Attendance,
                                        },
                                        loadComponent: () =>
                                            import(
                                                '../../components/social-service/setting/service-settings-attendance/service-settings-attendance.component'
                                                ).then(
                                                (m) => m.ServiceSettingsAttendanceComponent,
                                            ),
                                    },
                                    {
                                        path: 'continuous-records',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.WorkContinuousRecords,
                                        },
                                        component:
                                        ServiceSettingContinuousRecordPageComponent,
                                    },
                                    {
                                        path: 'movement-restriction',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.WorkMovementRestriction,
                                        },
                                        component:
                                        ServiceSettingsMovementRestrictionComponent,
                                    },
                                    {
                                        path: 'tangible-deposits',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: BreadcrumbsEnum.WorkTangibleDeposits,
                                        },
                                        loadComponent: () =>
                                            import(
                                                '../../components/social-service/setting/service-settings-tangible-deposits/service-settings-tangible-deposits.component'
                                                ).then((m) => m.ServiceSettingsTangibleDepositsComponent),
                                    },
                                    {
                                        path: 'sanction',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.WorkSanction,
                                        },
                                        component:
                                        ServiceSettingSanctionComponent,
                                    },
                                    {
                                        path: 'suggestion',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.Suggestion,
                                        },
                                        component: ServiceSettingSuggestionComponent,
                                    },
                                    {
                                        path: 'doctor-record',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsDoctorRecord,
                                        },
                                        component:
                                        ServiceSettingsDoctorRecordComponent,
                                    },
                                    {
                                        path: 'continuous-records',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.WorkContinuousRecords,
                                        },
                                        component:
                                        ServiceSettingContinuousRecordPageComponent,
                                    },
                                    {
                                        path: 'specialist-record',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: {
                                                label: BreadcrumbsEnum.SpecialistRecords,
                                                alias: BreadcrumbsEnum.SpecialistRecords,
                                            },
                                        },
                                        component:
                                        ServiceSettingsSpecialistRecordComponent,
                                    },
                                    {
                                        path: 'risk-type',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsRiskType,
                                        },
                                        component:
                                        ServiceSettingsRiskTypeComponent,
                                    },
                                    {
                                        path: 'scheduler',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsScheduler,
                                        },
                                        component:
                                        ServiceSettingsSchedulerComponent,
                                    },
                                    {
                                        path: 'diet-setting',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: BreadcrumbsEnum.ServiceDietSetting,
                                        },
                                        loadComponent: () => import('../../components/social-service/setting/service-setting-diet-setting/service-setting-diet-setting.component').then(m => m.ServiceSettingDietSettingComponent),
                                    },
                                    {
                                        path: 'measured-values',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsMeasuredValues,
                                        },
                                        component:
                                        ServiceSettingsMeasuredValuesComponent,
                                    },
                                    {
                                        path: 'documents',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsDocuments,
                                        },
                                        component:
                                        ServiceSettingsDocumentsComponent,
                                    },
                                    {
                                        path: 'social-works',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsSocialWorks,
                                        },
                                        loadComponent: () => import('./pages/services/settings/service-settings-social-work/service-settings-social-work.component').then((m) => m.ServiceSettingsSocialWorkComponent),
                                    },
                                    {
                                        path: 'operational-reports',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceOperationalReports,
                                        },
                                        component:
                                        ServiceOperationalReportsPageComponent,
                                    },
                                    {
                                        path: 'mail',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: BreadcrumbsEnum.ServiceSettingsMail,
                                        },
                                        loadComponent: () => import('./pages/services/settings/service-settings-mail/service-settings-mail.component').then((m) => m.ServiceSettingsMailComponent),
                                    },
                                    {
                                        path: 'user-tasks',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: BreadcrumbsEnum.UserTasks,
                                        },
                                        loadComponent: () => import('../../components/social-service/setting/service-settings-user-task/service-settings-user-task.component').then((m) => m.ServiceSettingsUserTaskComponent),
                                    },
                                    {
                                        path: 'user-tasks',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: BreadcrumbsEnum.UserTasks,
                                        },
                                        loadComponent: () => import('../../components/social-service/setting/service-settings-user-task/service-settings-user-task.component').then((m) => m.ServiceSettingsUserTaskComponent),
                                    },
                                    {
                                        path: 'basic-social-consulting',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: BreadcrumbsEnum.BasicSocialConsulting,
                                        },
                                        loadComponent: () => import('../../components/social-service/setting/service-setting-basic-social-consulting/service-setting-basic-social-consulting.component').then((m) => m.ServiceSettingBasicSocialConsultingComponent),
                                    },
                                    {
                                        path: 'provided-operations-reports',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: BreadcrumbsEnum.BasicSocialConsulting,
                                        },
                                        loadComponent: () =>
                                            import(
                                                '../../components/social-service/setting/service-setting-provided-operations-reports/service-setting-provided-operations-reports.component'
                                                ).then((m) => m.ServiceSettingProvidedOperationsReportsComponent),
                                    },
                                    {
                                        path: 'warehouse-material',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.WarehouseMaterial,
                                        },
                                        loadComponent: () => import('../../components/social-service/setting/service-settings-warehouse-material/service-settings-warehouse-material.component').then((m) => m.ServiceSettingsWarehouseMaterialComponent),
                                    },
                                    {
                                        path: 'service-announcements',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceAnnouncements,
                                        },
                                        loadComponent: () => import('../../components/social-service/setting/service-setting-service-announcements/service-setting-service-announcements.component').then((m) => m.ServiceSettingServiceAnnounecementsComponent),
                                    },
                                    {
                                        path: 'social-payment',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingSocialPayments,
                                        },
                                        loadComponent: () => import('../../components/social-service/setting/social-service-service-setting-payment/social-service-service-setting-payment.component').then((m) => m.SocialServiceServiceSettingPaymentComponent),
                                    },
                                    {
                                        path: 'social-billing',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingSocialBilling,
                                        },
                                        loadComponent: () => import('../../components/social-service/setting/social-service-service-setting-billing/social-service-service-setting-billing.component').then((m) => m.SocialServiceServiceSettingBillingComponent),
                                    },
                                    {
                                        path: 'financial-deposit',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingFinancialDeposit,
                                        },
                                        loadComponent: () => import('../../components/social-service/setting/social-service-settings-financial-deposits/social-service-settings-financial-deposits.component').then((m) => m.SocialServiceSettingsFinancialDepositsComponent),
                                    },
                                    {
                                        path: 'epoukaz',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceSettingsEpoukaz,
                                        },
                                        component:
                                        ServiceSettingsEpoukazComponent,
                                    },
                                    {
                                        path: 'nursing-allowance',
                                        title: generalName,
                                        canMatch: [() => Boolean(featureOn.NURSING_ALLOWANCE)],
                                        data: { breadcrumb: BreadcrumbsEnum.ServiceSettingsNursingAllowance },
                                        loadComponent: () => import(
                                            './pages/services/settings/service-settings-nursing-allowance/service-settings-nursing-allowance.component'
                                            ).then(m => m.ServiceSettingsNursingAllowanceComponent),
                                    },
                                    {
                                        path: 'credits',
                                        title: xCarolName,
                                        canMatch: [() => Boolean(featureOn.SOCIAL_CREDITS)],
                                        data: { breadcrumb: BreadcrumbsEnum.ServiceSettingsCredits },
                                        loadComponent: () => import(
                                            '../../components/social-service/setting/social-service-settings-credits/social-service-settings-credits.component'
                                        ).then(m => m.SocialServiceSettingsCreditsComponent),
                                    },
                                    {
                                        path: 'ereferrals',
                                        title: generalName,
                                        canMatch: [() => Boolean(featureOn.EREFERRALS)],
                                        data: { breadcrumb: BreadcrumbsEnum.ServiceSettingsEReferrals },
                                        loadComponent: () => import(
                                            './pages/services/settings/service-settings-ereferrals/service-settings-ereferrals.component'
                                        ).then(m => m.ServiceSettingsEReferralsComponent),
                                    },
                                ],
                            },
                            {
                                path: 'groups',
                                title: xCarolName,
                                data: {
                                    trashEntityName: TrashEntityTypeEnum.SERVICE_GROUP,
                                    breadcrumb: BreadcrumbsEnum.ServiceGroups,
                                },
                                component: ServicesGroupsPageComponent,
                            },
                            {
                                path: 'projects',
                                title: xCarolName,
                                data: {
                                    breadcrumb: BreadcrumbsEnum.ServiceSocialProjects,
                                },
                                loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/social-projects-page.component').then((m) => m.SocialProjectsPageComponent),
                            },
                            {
                                path: 'projects',
                                title: xCarolName,
                                data: {
                                    breadcrumb: BreadcrumbsEnum.ServiceSocialProjects,
                                },
                                children: [
                                    {
                                        path: 'create',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: BreadcrumbsEnum.ServiceSocialProjectAdd,
                                        },
                                        loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/social-project-detail-page/social-project-detail-page.component').then((m) => m.SocialProjectDetailPageComponent),
                                    },
                                    {
                                        // Detail projektu je rodičem svých podstránek, aby v breadcrumbu zůstal proklik zpět na detail.
                                        path: ':projectId',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: {
                                                label: BreadcrumbsEnum.ServiceSocialProject,
                                                alias: BreadcrumbsEnum.ServiceSocialProject,
                                            },
                                        },
                                        children: [
                                            {
                                                path: '',
                                                pathMatch: 'full',
                                                title: xCarolName,
                                                loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/social-project-detail-page/social-project-detail-page.component').then((m) => m.SocialProjectDetailPageComponent),
                                            },
                                            {
                                                path: 'clients',
                                                title: xCarolName,
                                                data: {
                                                    breadcrumb: {
                                                        label: BreadcrumbsEnum.ServiceSocialProjectClients,
                                                        alias: BreadcrumbsEnum.ServiceSocialProjectClients,
                                                    },
                                                },
                                                loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/social-project-clients-page/social-project-clients-page.component').then((m) => m.SocialProjectClientsPageComponent),
                                            },
                                            {
                                                path: 'clients/create',
                                                title: xCarolName,
                                                data: {
                                                    breadcrumb: {
                                                        label: BreadcrumbsEnum.ServiceSocialProjectClientAdd,
                                                        alias: BreadcrumbsEnum.ServiceSocialProjectClientAdd,
                                                    },
                                                },
                                                loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/social-project-client-add-page/social-project-client-add-page.component').then((m) => m.SocialProjectClientAddPageComponent),
                                            },
                                            {
                                                path: 'clients/:id',
                                                title: xCarolName,
                                                data: {
                                                    breadcrumb: {
                                                        label: BreadcrumbsEnum.ServiceSocialProjectClient,
                                                        alias: BreadcrumbsEnum.ServiceSocialProjectClient,
                                                    },
                                                },
                                                loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/social-project-client-detail-page/social-project-client-detail-page.component').then((m) => m.SocialProjectClientDetailPageComponent),
                                            },
                                            {
                                                path: 'clients/:id/work',
                                                title: xCarolName,
                                                data: {
                                                    breadcrumb: {
                                                        label: BreadcrumbsEnum.ServiceSocialProjectClientWork,
                                                        alias: BreadcrumbsEnum.ServiceSocialProjectClientWork,
                                                    },
                                                },
                                                loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/social-project-client-work-page/social-project-client-work-page.component').then((m) => m.SocialProjectClientWorkPageComponent),
                                            },
                                            {
                                                path: 'clients/:clientId/monitoring-sheets/create',
                                                title: xCarolName,
                                                data: {
                                                    breadcrumb: {
                                                        label: BreadcrumbsEnum.ServiceSocialProjectMonitoringSheet,
                                                        alias: BreadcrumbsEnum.ServiceSocialProjectMonitoringSheet,
                                                    },
                                                },
                                                loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/monitoring-sheet-detail-page/monitoring-sheet-detail-page.component').then((m) => m.MonitoringSheetDetailPageComponent),
                                            },
                                            {
                                                path: 'clients/:clientId/monitoring-sheets/:id',
                                                title: xCarolName,
                                                data: {
                                                    breadcrumb: {
                                                        label: BreadcrumbsEnum.ServiceSocialProjectMonitoringSheet,
                                                        alias: BreadcrumbsEnum.ServiceSocialProjectMonitoringSheet,
                                                    },
                                                },
                                                loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/monitoring-sheet-detail-page/monitoring-sheet-detail-page.component').then((m) => m.MonitoringSheetDetailPageComponent),
                                            },
                                            {
                                                path: 'clients/:clientId/monitoring-sheets/:id/work',
                                                title: xCarolName,
                                                data: {
                                                    breadcrumb: {
                                                        label: BreadcrumbsEnum.ServiceSocialProjectMonitoringSheetWork,
                                                        alias: BreadcrumbsEnum.ServiceSocialProjectMonitoringSheetWork,
                                                    },
                                                },
                                                loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/monitoring-sheet-work-page/monitoring-sheet-work-page.component').then((m) => m.MonitoringSheetWorkPageComponent),
                                            },
                                            {
                                                path: 'workloads',
                                                title: xCarolName,
                                                data: {
                                                    breadcrumb: {
                                                        label: BreadcrumbsEnum.ServiceSocialProjectWorkloads,
                                                        alias: BreadcrumbsEnum.ServiceSocialProjectWorkloads,
                                                    },
                                                },
                                                loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/social-project-users-page/social-project-users-page.component').then((m) => m.SocialProjectUsersPageComponent),
                                            },
                                            {
                                                path: 'workloads/create',
                                                title: xCarolName,
                                                data: {
                                                    breadcrumb: {
                                                        label: BreadcrumbsEnum.ServiceSocialProjectWorkloadAdd,
                                                        alias: BreadcrumbsEnum.ServiceSocialProjectWorkloadAdd,
                                                    },
                                                },
                                                loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/social-project-user-add-page/social-project-user-add-page.component').then((m) => m.SocialProjectUserAddPageComponent),
                                            },
                                            {
                                                path: 'workloads/:id',
                                                title: xCarolName,
                                                data: {
                                                    breadcrumb: {
                                                        label: BreadcrumbsEnum.ServiceSocialProjectWorkload,
                                                        alias: BreadcrumbsEnum.ServiceSocialProjectWorkload,
                                                    },
                                                },
                                                loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/social-project-user-detail-page/social-project-user-detail-page.component').then((m) => m.SocialProjectUserDetailPageComponent),
                                            },
                                            {
                                                path: 'work',
                                                title: xCarolName,
                                                data: {
                                                    breadcrumb: {
                                                        label: BreadcrumbsEnum.ServiceSocialProjectWork,
                                                        alias: BreadcrumbsEnum.ServiceSocialProjectWork,
                                                    },
                                                },
                                                loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/social-project-work-page/social-project-work-page.component').then((m) => m.SocialProjectWorkPageComponent),
                                            },
                                            {
                                                path: 'reports/:reportType',
                                                title: xCarolName,
                                                data: {
                                                    breadcrumb: {
                                                        label: BreadcrumbsEnum.ServiceSocialProjectReport,
                                                        alias: BreadcrumbsEnum.ServiceSocialProjectReport,
                                                    },
                                                },
                                                loadComponent: () => import('../../components/social-service/setting/service-setting-social-projects/social-project-report-detail-page/social-project-report-detail-page.component').then((m) => m.SocialProjectReportDetailPageComponent),
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: 'social-areas',
                                title: xCarolName,
                                data: {
                                    breadcrumb: BreadcrumbsEnum.ServiceSocialAreas,
                                },
                                loadComponent: () => import('./pages/services/settings/service-settings-social-area/service-settings-social-area.component').then(m => m.ServiceSettingsSocialAreaComponent),
                            },
                            {
                                path: 'procedure',
                                title: xCarolName,
                                data: {
                                    trashEntityName: TrashEntityTypeEnum.SERVICE_MEDICAL_PROCEDURE,
                                    breadcrumb:
                                    BreadcrumbsEnum.ServiceMedicalProcedures,
                                },
                                component: ServicesProceduresPageComponent,
                            },
                            {
                                path: 'procedure',
                                title: xCarolName,
                                data: {
                                    breadcrumb:
                                    BreadcrumbsEnum.ServiceMedicalProcedures,
                                },
                                children: [
                                    {
                                        path: ':serviceMedicalProcedureId',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: {
                                                label: BreadcrumbsEnum.ServiceMedicalProcedure,
                                                alias: BreadcrumbsEnum.ServiceMedicalProcedure,
                                            },
                                        },
                                        component:
                                        ServicesProceduresDetailPageComponent,
                                    },
                                ],
                            },
                            {
                                path: 'offers',
                                title: xCarolName,
                                data: {
                                    breadcrumb: BreadcrumbsEnum.ServiceOffer,
                                },
                                component: ServiceSettingsOffersPageComponent,
                            },
                            {
                                path: 'offers',
                                title: xCarolName,
                                data: {
                                    breadcrumb: BreadcrumbsEnum.ServiceOffers,
                                },
                                children: [
                                    {
                                        path: ':offerId',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceOffer,
                                        },
                                        component: ServiceSettingsOfferDetailPageComponent,
                                    },
                                ],
                            },
                            {
                                path: 'diet',
                                title: xCarolName,
                                data: {
                                    breadcrumb: BreadcrumbsEnum.ServiceDiet,
                                },
                                loadComponent: () => import('../../components/social-service/setting/service-setting-diet/service-setting-diet.component').then(m => m.ServiceSettingDietComponent),
                            },
                            {
                                path: 'payments',
                                title: xCarolName,
                                data: {
                                    trashEntityName: TrashEntityTypeEnum.SERVICE_PAYMENT,
                                    breadcrumb: BreadcrumbsEnum.ServicePayments,
                                },
                                component: ServicesPaymentsPageComponent,
                            },
                            {
                                path: 'payments',
                                title: xCarolName,
                                data: {
                                    breadcrumb: BreadcrumbsEnum.ServicePayments,
                                },
                                children: [
                                    {
                                        path: ':paymentId',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: {
                                                label: BreadcrumbsEnum.ServicePayment,
                                                alias: BreadcrumbsEnum.ServicePayment,
                                            },
                                        },
                                        component:
                                        ServicesPaymentsDetailPageComponent,
                                    },
                                ],
                            },
                            {
                                path: 'workplaces',
                                title: xCarolName,
                                data: { breadcrumb: { skip: true } },
                                children: [
                                    {
                                        path: ':workplaceId',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: {
                                                label: BreadcrumbsEnum.Workplace,
                                                alias: BreadcrumbsEnum.Workplace,
                                            },
                                        },
                                        component: WorkplaceDetailPageComponent,
                                    },
                                ],
                            },
                            {
                                path: 'warehouse-materials',
                                title: xCarolName,
                                data: {
                                    breadcrumb: BreadcrumbsEnum.Warehouse,
                                },
                                loadComponent: () => import('./pages/services/service-warehouse-materials-page/service-warehouse-materials-page.component').then(m => m.ServiceWarehouseMaterialsPageComponent),
                            },
                            {
                                path: 'warehouse-materials',
                                title: xCarolName,
                                data: { breadcrumb: BreadcrumbsEnum.Warehouse },
                                children: [
                                    {
                                        path: ':warehouseMaterialId',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: {
                                                label: BreadcrumbsEnum.WarehouseMaterial,
                                            },
                                        },
                                        loadComponent: () => import('./pages/services/service-warehouse-material-edit-page/service-warehouse-material-edit-page.component').then(m => m.ServiceWarehouseMaterialEditPageComponent),
                                    },
                                ],
                            },

                            {
                                path: 'patterns',
                                title: xCarolName,
                                data: {
                                    trashEntityName: TrashEntityTypeEnum.SERVICE_PATTERN,
                                    breadcrumb: BreadcrumbsEnum.ServicePatterns,
                                },
                                component: ServicePatternsPageComponent,
                            },
                            {
                                path: 'patterns',
                                title: xCarolName,
                                data: {
                                    breadcrumb: BreadcrumbsEnum.ServicePatterns,
                                },
                                children: [
                                    {
                                        path: 'structures/create',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServicePatternStructureAdd,
                                        },
                                        component:
                                        ServicesPatternsStructurePageComponent,
                                    },
                                    {
                                        path: 'structures/:patternId',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: {
                                                label: BreadcrumbsEnum.ServicePatternsStructures,
                                                alias: BreadcrumbsEnum.ServicePatternsStructures,
                                            },
                                        },
                                        component:
                                        ServicesPatternsStructurePageComponent,
                                    },
                                    {
                                        path: 'documents/create',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServicePatternsDocumentAdd,
                                        },
                                        component:
                                        ServicesPatternsDocumentPageComponent,
                                    },
                                    {
                                        path: 'documents/:patternId',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: {
                                                label: BreadcrumbsEnum.ServicePatternsDocuments,
                                                alias: BreadcrumbsEnum.ServicePatternsDocuments,
                                            },
                                        },
                                        component:
                                        ServicesPatternsDocumentPageComponent,
                                    },
                                ],
                            },
                            {
                                path: 'operational-reports',
                                title: xCarolName,
                                data: {
                                    breadcrumb:
                                    BreadcrumbsEnum.ServiceOperationalReports,
                                },
                                component:
                                ServiceOperationalReportsPageComponent,
                            },
                            {
                                path: 'nursing-diagnoses',
                                title: xCarolName,
                                data: {
                                    trashEntityName: TrashEntityTypeEnum.SERVICE_NURSING_CARE_DIAGNOSIS_CODE,
                                    breadcrumb:
                                    BreadcrumbsEnum.ServiceNursingDiagnoses,
                                },
                                component: ServiceNursingDiagnosesPageComponent,
                            },
                            {
                                path: 'nursing-diagnoses',
                                title: xCarolName,
                                data: {
                                    breadcrumb:
                                    BreadcrumbsEnum.ServiceNursingDiagnoses,
                                },
                                children: [
                                    {
                                        path: 'create',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb:
                                            BreadcrumbsEnum.ServiceNursingDiagnosisAdd,
                                        },
                                        component:
                                        ServiceNursingDiagnosisDetailPageComponent,
                                    },
                                    {
                                        path: ':nursingDiagnosisId',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: {
                                                label: BreadcrumbsEnum.ServiceNursingDiagnosis,
                                                alias: BreadcrumbsEnum.ServiceNursingDiagnosis,
                                            },
                                        },
                                        component:
                                        ServiceNursingDiagnosisDetailPageComponent,
                                    },
                                ],
                            },
                            {
                                path: 'logs',
                                title: xCarolName,
                                data: {
                                    breadcrumb: BreadcrumbsEnum.ServiceLogs,
                                },
                                component: MedicalServiceLogsPageComponent,
                            },
                        ],
                    },
                ],
            },
            {
                path: 'system',
                title: xCarolName,
                data: { breadcrumb: BreadcrumbsEnum.System },
                component: SystemSettingsPageComponent,
            },
            {
                path: 'system',
                title: xCarolName,
                data: {
                    visit: { group: true },
                    breadcrumb: BreadcrumbsEnum.System,
                },
                component: SystemSettingsPageComponent,
                children: [
                    {
                        path: 'service-type',
                        title: xCarolName,
                        data: { breadcrumb: BreadcrumbsEnum.ServiceTypes },
                        component: ServiceTypesPageComponent,
                    },
                    {
                        path: 'service-type',
                        title: xCarolName,
                        data: { breadcrumb: BreadcrumbsEnum.ServiceTypes },
                        children: [
                            {
                                path: ':serviceTypeId',
                                title: xCarolName,
                                data: {
                                    breadcrumb: {
                                        label: BreadcrumbsEnum.ServiceType,
                                        alias: BreadcrumbsEnum.ServiceType,
                                    },
                                },
                                component: ServiceTypeDetailPageComponent,
                                children: [
                                    {
                                        path: 'offers',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: {
                                                label: BreadcrumbsEnum.ServiceTypeOffers,
                                                alias: BreadcrumbsEnum.ServiceTypeOffers,
                                            },
                                        },
                                        component:
                                        ServiceTypeOffersPageComponent,
                                    },
                                    {
                                        path: 'settings',
                                        title: xCarolName,
                                        data: {
                                            breadcrumb: {
                                                label: BreadcrumbsEnum.ServiceTypeSettings,
                                                alias: BreadcrumbsEnum.ServiceTypeSettings,
                                            },
                                        },
                                        component:
                                        ServiceTypeSettingsPageComponent,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: 'diagnosis',
                        title: xCarolName,
                        data: { breadcrumb: BreadcrumbsEnum.Diagnoses },
                        loadComponent: () =>
                            import(
                                '../../components/system-setting/diagnosis-bonification/diagnoses-page/diagnoses-page.component'
                                ).then((m) => m.DiagnosesPageComponent),
                    },
                    {
                        path: 'diagnosis',
                        title: xCarolName,
                        data: { breadcrumb: BreadcrumbsEnum.Diagnoses },
                        children: [{
                            path: ':code',
                            title: xCarolName,
                            data: {
                                breadcrumb: {
                                    label: BreadcrumbsEnum.Diagnosis,
                                    alias: BreadcrumbsEnum.Diagnosis,
                                },
                            },
                            loadComponent: () =>
                                import(
                                    '../../components/system-setting/diagnosis-bonification/diagnosis-detail-page/diagnosis-detail-page.component'
                                    ).then((m) => m.DiagnosisDetailPageComponent),
                        }],
                    },
                    {
                        path: 'data-interface-version',
                        title: xCarolName,
                        data: { breadcrumb: BreadcrumbsEnum.DataInterfaceVersions },
                        loadComponent: () =>
                            import(
                                '../../components/system-setting/datainterface-version/datainterface-versions-page/datainterface-versions-page.component'
                                ).then((m) => m.DataInterfaceVersionsPageComponent),
                    },
                    {
                        path: 'doctor-specialization',
                        title: xCarolName,
                        data: { breadcrumb: BreadcrumbsEnum.DoctorSpecializations },
                        loadComponent: () =>
                            import(
                                '../../components/system-setting/doctor-specialization/doctor-specializations-page/doctor-specializations-page.component'
                                ).then((m) => m.DoctorSpecializationsPageComponent),
                    },
                    {
                        path: 'puro',
                        title: xCarolName,
                        data: { breadcrumb: BreadcrumbsEnum.SystemPuro },
                        loadComponent: () =>
                            import(
                                '../../components/system-setting/system-puro-setting/system-puro-setting.component'
                                ).then((m) => m.SystemPuroSettingComponent),
                    },
                    {
                        path: 'pmup',
                        title: xCarolName,
                        data: { breadcrumb: BreadcrumbsEnum.SystemPmup },
                        loadComponent: () =>
                            import(
                                '../../components/system-setting/system-pmup-setting/system-pmup-setting.component'
                                ).then((m) => m.SystemPmupSettingComponent),
                    },
                    {
                        path: 'guide',
                        title: xCarolName,
                        data: { breadcrumb: BreadcrumbsEnum.SystemGuide },
                        loadComponent: () =>
                            import(
                                '../../components/system-setting/system-guide-setting/system-guide-setting.component'
                                ).then((m) => m.SystemGuideSettingComponent),
                    },
                    {
                        path: 'user-messages',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.MESSAGE,
                            breadcrumb: BreadcrumbsEnum.UserMessages,
                        },
                        loadComponent: () =>
                            import(
                                '../../components/system-setting/message/user-messages-page/user-messages-page.component'
                                ).then((m) => m.UserMessagesPageComponent),
                    },
                    {
                        path: 'user-messages',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.MESSAGE,
                            breadcrumb: BreadcrumbsEnum.UserMessages,
                        },
                        children: [
                            {
                                path: 'create',
                                title: xCarolName,
                                loadComponent: () =>
                                    import(
                                        '../../components/system-setting/message/user-message-detail-page/user-message-detail-page.component'
                                        ).then((m) => m.UserMessageDetailPageComponent),
                            },
                            {
                                path: ':id',
                                title: xCarolName,
                                data: {
                                    breadcrumb: {
                                        label: BreadcrumbsEnum.MessageTitle,
                                        alias: BreadcrumbsEnum.MessageTitle,
                                    },
                                },
                                loadComponent: () =>
                                    import(
                                        '../../components/system-setting/message/user-message-detail-page/user-message-detail-page.component'
                                        ).then((m) => m.UserMessageDetailPageComponent),
                            },
                        ],
                    },
                    {
                        path: 'methodical-messages',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.MESSAGE,
                            breadcrumb: BreadcrumbsEnum.MethodicalMessages,
                        },
                        loadComponent: () =>
                            import(
                                '../../components/system-setting/message/methodical-messages-page/methodical-messages-page.component'
                                ).then((m) => m.MethodicalMessagesPageComponent),
                    },
                    {
                        path: 'methodical-messages',
                        title: xCarolName,
                        data: {
                            trashEntityName: TrashEntityTypeEnum.MESSAGE,
                            breadcrumb: BreadcrumbsEnum.MethodicalMessages,
                        },
                        children: [
                            {
                                path: 'create',
                                title: xCarolName,
                                loadComponent: () =>
                                    import(
                                        '../../components/system-setting/message/methodical-message-detail-page/methodical-message-detail-page.component'
                                        ).then((m) => m.MethodicalMessageDetailPageComponent),
                            },
                            {
                                path: ':id',
                                title: xCarolName,
                                data: {
                                    breadcrumb: {
                                        label: BreadcrumbsEnum.MessageTitle,
                                        alias: BreadcrumbsEnum.MessageTitle,
                                    },
                                },
                                loadComponent: () =>
                                    import(
                                        '../../components/system-setting/message/methodical-message-detail-page/methodical-message-detail-page.component'
                                        ).then((m) => m.MethodicalMessageDetailPageComponent),
                            },
                        ],
                    },
                    {
                        path: 'background-tasks',
                        title: xCarolName,
                        data: { breadcrumb: BreadcrumbsEnum.BackgroundTasks },
                        loadComponent: () =>
                            import(
                                '../../components/system-setting/system-background-task-settings/system-background-task-settings.component'
                                ).then((m) => m.SystemBackgroundTaskSettingsComponent),
                    },
                    {
                        path: 'statistics',
                        title: xCarolName,
                        data: { breadcrumb: BreadcrumbsEnum.Statistics },
                        loadComponent: () =>
                            import(
                                '../../components/system-setting/system-statistics-settings-page/system-statistics-settings-page.component'
                                ).then((m) => m.SystemStatisticsSettingsPageComponent),
                    },
                    {
                        path: 'trash',
                        title: xCarolName,
                        data: { breadcrumb: BreadcrumbsEnum.Trash },
                        loadComponent: () =>
                            import(
                                '../../components/system-setting/system-trash-settings/system-trash-settings.component'
                                ).then((m) => m.SystemTrashSettingsComponent),
                    },
                    {
                        path: 'platform-billing',
                        title: xCarolName,
                        data: { breadcrumb: BreadcrumbsEnum.PlatformBilling },
                        loadComponent: () =>
                            import(
                                '../../components/system-setting/system-platform-billing/system-platform-billing.component'
                                ).then((m) => m.SystemPlatformBillingComponent),
                    },
                    {
                        path: 'platform-billing/:section',
                        redirectTo: 'platform-billing',
                    },
                ],
            },
            {
                path: 'users',
                title: generalName,
                data: { breadcrumb: BreadcrumbsEnum.Users },
                component: UsersPageComponent,
            },
            {
                path: 'users',
                title: generalName,
                data: { breadcrumb: BreadcrumbsEnum.Users },
                children: [
                    {
                        path: ':id',
                        title: generalName,
                        data: {
                            breadcrumb: {
                                label: BreadcrumbsEnum.User,
                                alias: BreadcrumbsEnum.User,
                            },
                        },
                        component: UserPageComponent,
                    },
                ],
            },
            {
                path: 'profile',
                title: generalName,
                data: {
                    visit: { group: true },
                    breadcrumb: {
                        label: 'breadcrumbs.profile',
                        skip: true,
                    },
                },
                component: ProfilePageComponent,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'account',
                    },
                    {
                        path: 'account',
                        title: generalName,
                        data: {
                            breadcrumb: {
                                label: 'user.profile.account',
                                skip: true,
                            },
                        },
                        loadComponent: () =>
                            import(
                                './components/user-management/profile-page-edit/profile-page-edit.component'
                                ).then((m) => m.ProfilePageEditComponent),
                    },
                    {
                        path: 'preferences',
                        title: generalName,
                        data: {
                            breadcrumb: {
                                label: 'user.profile.preferences.title',
                                skip: true,
                            },
                        },
                        loadComponent: () =>
                            import(
                                './pages/profile-page/profile-preferences/profile-preferences.component'
                                ).then((m) => m.ProfilePreferencesComponent),
                    },
                    {
                        path: 'security',
                        title: generalName,
                        data: {
                            breadcrumb: {
                                label: 'user.profile.security',
                                skip: true,
                            },
                        },
                        loadComponent: () =>
                            import(
                                './pages/profile-page/profile-security/profile-security.component'
                                ).then((m) => m.ProfileSecurityComponent),
                    },
                    {
                        path: 'msg',
                        title: generalName,
                        data: {
                            breadcrumb: {
                                label: 'user.profile.messages',
                                skip: true,
                            },
                        },
                        loadComponent: () =>
                            import(
                                './pages/profile-page/profile-messages/profile-messages.component'
                                ).then((m) => m.ProfileMessagesComponent),
                    },
                    {
                        path: 'msg/:id',
                        title: generalName,
                        data: {
                            breadcrumb: {
                                label: 'user.profile.messages',
                                skip: true,
                            },
                        },
                        loadComponent: () =>
                            import(
                                './pages/profile-page/profile-messages/message-recipient-detail-page/message-recipient-detail-page.component'
                                ).then((m) => m.MessageRecipientDetailPageComponent),
                    },
                    {
                        path: 'wysiwyg',
                        title: generalName,
                        data: {
                            breadcrumb: {
                                label: 'user.profile.wysiwyg.title',
                                skip: true,
                            },
                        },
                        loadComponent: () =>
                            import(
                                './pages/profile-page/wysiwyg-settings/wysiwyg-settings.component'
                                ).then((m) => m.WysiwygSettingsComponent),
                    },
                    {
                        path: 'electronic-signature',
                        title: generalName,
                        data: {
                            breadcrumb: {
                                label: 'user.profile.electronicSignature.title',
                                skip: true,
                            },
                        },
                        loadComponent: () =>
                            import(
                                './pages/profile-page/electronic-signature-settings/electronic-signature-settings.component'
                                ).then((m) => m.ElectronicSignatureSettingsComponent),
                    },
                    ...(featureOn.SOCIAL_SERVICE_DASHBOARD ? [{
                        path: 'dashboard',
                        title: generalName,
                        data: {
                            breadcrumb: {
                                label: 'user.profile.dashboardSettings.title',
                                skip: true,
                            },
                        },
                        loadComponent: () =>
                            import(
                                './pages/profile-page/profile-dashboard-settings/profile-dashboard-settings.component'
                                ).then((m) => m.ProfileDashboardSettingsComponent),
                    }] : []),
                ],
            },
        ],
    },
];
